// The platform-wide permission catalog (spec 16 §2.8). This file is the
// catalog of record: the `permissions` DB table is seeded from it (migration
// 0002) and an integration parity test fails if the two drift.
//
// PR #6 registers the rows module 16 OWNS: `identity.*`, `sync.*` (registered
// on module 18's behalf) and `platform.*`. Foreign-module namespaces
// (catalogue, production, …) are registered by their own modules' migrations
// later — the catalog is additive, and each module's migration also adds the
// §2.8 default role grants for its namespace.

/** `<namespace>.<verb_noun>` with an optional third dotted segment (spec 16 §2.8). */
export const PERMISSION_CODE_GRAMMAR = /^[a-z]+(\.[a-z][a-z0-9_]+){1,2}$/;

/** Module namespaces (spec 16 §2.8 namespace → module map). */
export const permissionNamespaces = [
  "identity",
  "catalogue",
  "onboarding",
  "planning",
  "production",
  "waste",
  "inventory",
  "quality",
  "maintenance",
  "sales",
  "procurement",
  "finance",
  "approvals",
  "exceptions",
  "messaging",
  "analytics",
  "agents",
  "billing",
  "platform",
  "sync",
] as const;

export type PermissionNamespace = (typeof permissionNamespaces)[number];

export interface PermissionDef {
  code: string;
  namespace: PermissionNamespace;
  description: string;
  /** true = only unlocks redacted fields; gates no endpoint by itself. */
  isFieldGrant: boolean;
  /** true = grantable only to platform staff contexts (never in tenant roles). */
  isPlatformOnly: boolean;
}

function perm(
  code: string,
  namespace: PermissionNamespace,
  description: string,
  flags: { field?: boolean; platform?: boolean } = {},
): PermissionDef {
  return {
    code,
    namespace,
    description,
    isFieldGrant: flags.field ?? false,
    isPlatformOnly: flags.platform ?? false,
  };
}

/**
 * The rows module 16 owns (spec 16 §2.8): identity.*, sync.* and platform.*.
 */
export const permissionCatalog: readonly PermissionDef[] = [
  perm("identity.view_members", "identity", "List/see members + their roles and scopes"),
  // P3-UX UP6 (D-442): a co-member's display name where a record names them,
  // through the names read (GET /v1/refs, spec 16 §3.13) and nothing else.
  // It opens no member list, role, scope or contact detail; those stay
  // behind identity.view_members. Migration 0099 seeds it.
  perm(
    "identity.view_member_names",
    "identity",
    "See a co-member's display name where a record names them, through the names read; no member list, roles, scopes or contact details (spec 16 §3.13, D-442)",
  ),
  perm("identity.invite_member", "identity", "Create invites (scope-limited, spec 16 §7)"),
  perm(
    "identity.provision_member",
    "identity",
    "Create accounts directly (temp password, no email flow — spec 16 §3.5a)",
  ),
  perm("identity.update_member", "identity", "Change a member's role/scope"),
  perm("identity.revoke_member", "identity", "Revoke membership (+ cascade session kill)"),
  perm("identity.unlock_account", "identity", "Clear a member's login lockout"),
  perm("identity.view_roles", "identity", "View roles + their permission sets"),
  perm("identity.manage_roles", "identity", "Create/edit/delete custom roles"),
  perm("identity.view_sessions_any", "identity", "See any member's device list (tenant)"),
  perm("identity.revoke_session_any", "identity", "Revoke any member's sessions (tenant)"),
  perm(
    "identity.manage_security_policy",
    "identity",
    "Edit require_totp_roles, session_max_age_days",
  ),
  perm("identity.view_auth_events", "identity", "Read tenant auth event log"),
  perm("identity.view_org", "identity", "View facilities + departments + work centers"),
  perm("identity.manage_org", "identity", "Create/edit/archive facilities + departments"),
  perm(
    "identity.manage_work_centers",
    "identity",
    "Create/edit/archive work centers + their rates (spec 16 §2.19)",
  ),
  // Saved-view moderation (spec 16 §2.20, §3.12 item 8, §7; D-305 ruling 14).
  // Migration 0070 registers it and grants it to group_admin's system role
  // alone. The service counts it only from a group-scoped grant, so a custom
  // role holding it at facility or department scope moderates nothing. 0002
  // seeded the fifteen rows above; the parity gate in roles.int.test.ts
  // compares the catalog as a whole, whichever migration wrote each row.
  perm(
    "identity.manage_shared_views",
    "identity",
    "Delete any member's shared saved view, counted only from a group-scoped grant (moderation, spec 16 §3.12, §7; D-305)",
  ),
  // catalogue (module 01) — the full §7 permission set (17 codes). read +
  // product.write landed with the PR #8 walking skeleton; the P1 schema+contracts
  // PR registered the next fourteen (authoring, the three split product-lifecycle
  // codes activate/discontinue/reactivate, uom/schema/template management, import,
  // and the three cost codes); structure.read is the §7 "Read BOM structure" gate
  // (SECURITY-MED split from the card-level catalogue.read). All non-platform,
  // none field-only: cost.read gates whole cost endpoints AND drives §7
  // field-level redaction, so it is not a pure field grant.
  perm("catalogue.read", "catalogue", "View products, attributes + units of measure (spec 01 §7)"),
  perm(
    "catalogue.structure.read",
    "catalogue",
    "Read BOM/routing structure — versions, lines, steps + where-used (spec 01 §7)",
  ),
  perm("catalogue.product.write", "catalogue", "Create/edit product drafts (spec 01 §7)"),
  perm(
    "catalogue.product.activate",
    "catalogue",
    "Activate products (draft → active) (spec 01 §7)",
  ),
  perm(
    "catalogue.product.discontinue",
    "catalogue",
    "Discontinue products (active → discontinued) (spec 01 §7)",
  ),
  perm(
    "catalogue.product.reactivate",
    "catalogue",
    "Reactivate products (discontinued → active) (spec 01 §7)",
  ),
  perm("catalogue.bom.write", "catalogue", "Author BOM version drafts (spec 01 §7)"),
  perm("catalogue.bom.activate", "catalogue", "Activate/archive BOM versions (spec 01 §7)"),
  perm("catalogue.routing.write", "catalogue", "Author routing version drafts (spec 01 §7)"),
  perm("catalogue.routing.activate", "catalogue", "Activate/archive routing versions (spec 01 §7)"),
  perm(
    "catalogue.uom.manage",
    "catalogue",
    "Create/edit/archive units of measure + conversions (spec 01 §7)",
  ),
  perm("catalogue.schema.manage", "catalogue", "Create/edit attribute schemas (spec 01 §7)"),
  perm("catalogue.import.run", "catalogue", "Run catalogue Excel imports (spec 01 §7)"),
  perm(
    "catalogue.template.manage",
    "catalogue",
    "Create/edit/archive catalogue templates (spec 01 §7)",
  ),
  perm(
    "catalogue.cost.read",
    "catalogue",
    "Read standard costs, roll-ups, breakdowns + NRV (spec 01 §7)",
  ),
  perm("catalogue.cost.run", "catalogue", "Run standard cost roll-ups (spec 01 §7)"),
  perm("catalogue.cost.release", "catalogue", "Release standard cost runs (spec 01 §7)"),
  // onboarding (module 02) — the full §7 permission set (14 codes). view +
  // manage_wizard drive the wizard; install_pack / edit_terminology / go_live
  // are group-only governance (facility_admin excluded, §7); the six import.*
  // lanes gate per-kind imports (§7 matrix); generate_qr_kit + the two
  // checklist codes cover the QR kit + waive/manual-toggle. All non-platform,
  // none field-only (the money-field redaction on import rows is driven by
  // finance cost-visibility, spec 02 §7, not an onboarding field grant).
  perm(
    "onboarding.view",
    "onboarding",
    "View the onboarding wizard, session, steps + go-live checklist (spec 02 §7)",
  ),
  perm(
    "onboarding.manage_wizard",
    "onboarding",
    "Save step payloads, complete steps + commit the plant map (spec 02 §7)",
  ),
  perm(
    "onboarding.install_pack",
    "onboarding",
    "Install / switch the vertical template pack (spec 02 §7)",
  ),
  perm(
    "onboarding.edit_terminology",
    "onboarding",
    "Edit tenant terminology overrides (spec 02 §7)",
  ),
  perm(
    "onboarding.import.catalogue",
    "onboarding",
    "Run catalogue imports — products/materials/BOMs/routings (spec 02 §7)",
  ),
  perm(
    "onboarding.import.customers",
    "onboarding",
    "Run customer + open-sales-order imports (spec 02 §7)",
  ),
  perm(
    "onboarding.import.suppliers",
    "onboarding",
    "Run supplier + open-purchase-order imports (spec 02 §7)",
  ),
  perm(
    "onboarding.import.opening_stock",
    "onboarding",
    "Run opening-stock imports (commit posts value with the finance co-permission) (spec 02 §7)",
  ),
  perm("onboarding.import.machines", "onboarding", "Run machine imports (spec 02 §7)"),
  perm("onboarding.import.people", "onboarding", "Run people (invite) imports (spec 02 §7)"),
  perm(
    "onboarding.generate_qr_kit",
    "onboarding",
    "Generate QR print kits — stations, stock-unit + location labels (spec 02 §7)",
  ),
  perm(
    "onboarding.checklist.waive",
    "onboarding",
    "Waive go-live checklist items (blocking items require group_admin) (spec 02 §7)",
  ),
  perm(
    "onboarding.checklist.manual_set",
    "onboarding",
    "Toggle manual go-live checklist items passed/failed (spec 02 §7)",
  ),
  perm(
    "onboarding.go_live",
    "onboarding",
    "Waive blocking items + activate the tenant (go-live) (spec 02 §7)",
  ),
  // inventory (module 06) — the full §7 permission set: 15 action codes + the
  // inventory.cost.read FIELD grant (unit_cost_minor/currency + count variance
  // values, §7 field redactions). §7's "UoM conversions edit" row maps to
  // EXISTING module-01 codes (module 01 owns conversion tables, spec 06
  // §2.10): product-specific factors → catalogue.product.write (planner/FA/GA
  // hold it), generic conversion CRUD → catalogue.uom.manage (FA/GA only) —
  // deliberately no new code for it. Cell-level narrowings the static
  // guard cannot express (storekeeper ≤ threshold adjustments, planner/
  // procurement create-only transfers, qc qc_fail-only cuts, sales own-orders/
  // own-customers, floor_worker count-record-only) are service-enforced on top
  // of these codes — the same two-layer pattern as module 02's import lanes
  // (D-040 pt 1). Descriptions are seeded verbatim by migration 0008 (parity
  // gate, roles.int.test.ts).
  perm(
    "inventory.read",
    "inventory",
    "View stock: lookup, levels, lots, stock units, movements + ATP (spec 06 §7)",
  ),
  perm(
    "inventory.location.manage",
    "inventory",
    "Create/edit locations, re-parent + print location QR labels (spec 06 §7)",
  ),
  perm(
    "inventory.receipt.create",
    "inventory",
    "Register stock units + post receipts (spec 06 §7)",
  ),
  perm(
    "inventory.unit.cut",
    "inventory",
    "Cut measured stock units (qc: guided qc_fail cuts only, service-enforced) (spec 06 §7)",
  ),
  perm(
    "inventory.scan.locate",
    "inventory",
    "Scan to locate + re-measure stock units (spec 06 §7)",
  ),
  perm(
    "inventory.adjust",
    "inventory",
    "Post manual adjustments (storekeeper ≤ tenant value threshold, service-enforced) (spec 06 §7)",
  ),
  perm("inventory.movement.reverse", "inventory", "Reverse stock movements (spec 06 §7)"),
  perm(
    "inventory.reservation.manage",
    "inventory",
    "Create + release reservations (sales limited to own orders, service-enforced) (spec 06 §7)",
  ),
  perm(
    "inventory.transfer.manage",
    "inventory",
    "Create, edit + dispatch transfers (planner/procurement create-only, service-enforced) (spec 06 §7)",
  ),
  perm(
    "inventory.transfer.receive",
    "inventory",
    "Receive transfers at the destination facility (spec 06 §7)",
  ),
  perm(
    "inventory.quarantine",
    "inventory",
    "Quarantine + release lots and stock units (spec 06 §7)",
  ),
  perm(
    "inventory.count.perform",
    "inventory",
    "Create, start, record + submit cycle counts (floor_worker records counts only, service-enforced) (spec 06 §7)",
  ),
  perm(
    "inventory.count.review",
    "inventory",
    "Review count lines + approve cycle counts (spec 06 §7)",
  ),
  perm(
    "inventory.trace.read",
    "inventory",
    "Run genealogy/recall traces (sales limited to own customers, service-enforced) (spec 06 §7)",
  ),
  perm("inventory.count_schedule.manage", "inventory", "Manage cycle count schedules (spec 06 §7)"),
  perm(
    "inventory.cost.read",
    "inventory",
    "See unit_cost_minor/currency on movements, levels + history, and count variance values (spec 06 §7 field redaction)",
    { field: true },
  ),
  // waste (module 05) — spec 05 §7. SLICE 05-A registers the THREE codes it
  // enforces; the other twelve §7 codes (event.record, event.reverse,
  // disposition.set, cost.manual, remnant.manage, match.accept, pvc.manage,
  // regrind.manage, sale.create, sale.confirm, target.set, posting.view) are
  // registered by the slices that enforce them — a catalog code nothing checks
  // is a grant the product does not honour, and `GET /v1/permissions` would
  // offer it to a role that could not use it.
  //
  // The §7 matrix, cell by cell: "Edit categories/causes/classes/settings" is
  // group_admin ✓ and facility_admin "targets only (own F)" — so BOTH write
  // codes are group_admin-only, and spec 05 §5's category-edit row (which also
  // listed facility_admin) is corrected to match its own §7 in this PR (D-068).
  // "View ledger & variance" is ✓ for group_admin, facility_admin, planner,
  // finance, qc (no cost) and maintenance (machine variance only) — those six
  // hold `waste.read`; the parenthesised narrowings are serializer/service
  // rules on top of the code (D-040 two-layer), never separate codes.
  //
  // Catalog READS carry NO code at all: §5 rows 1 and 61 say "all
  // authenticated", because the floor capture grid and the remnant pickers are
  // driven by storekeepers and floor workers whose §7 cells on the view row are
  // "—". Descriptions are seeded verbatim by migration 0018 (parity gate,
  // roles.int.test.ts).
  perm(
    "waste.read",
    "waste",
    "View the waste ledger, variance, rollups + the waste catalogs and settings (qc sees no cost, maintenance machine variance only — service-enforced) (spec 05 §7)",
  ),
  perm(
    "waste.catalog.manage",
    "waste",
    "Create + edit waste categories, cause codes and remnant price classes (spec 05 §7)",
  ),
  perm(
    "waste.settings.manage",
    "waste",
    "Edit the tenant matcher/variance settings (group_admin only, audit-chained before/after) (spec 05 §7)",
  ),
  // SLICE 05-B (the ledger + capture + remnant intake) registers the FIVE codes
  // it enforces — the remaining seven §7 codes (match.accept, pvc.manage,
  // regrind.manage, sale.create, sale.confirm, target.set, posting.view) stay
  // with slices 05-C/05-D, the same one-slice-one-enforcement rule as above.
  // Descriptions are seeded verbatim by migration 0031 (parity gate,
  // roles.int.test.ts).
  perm(
    "waste.event.record",
    "waste",
    "Record a waste event through any §3.1 door (POST /v1/waste/events); source=manual is further narrowed to finance/facility_admin/group_admin service-side (spec 05 §7)",
  ),
  perm(
    "waste.event.reverse",
    "waste",
    "Create a reversal row for a waste event (POST /v1/waste/events/:id/reverse) — the pair nets to zero in every aggregate (spec 05 §7)",
  ),
  perm(
    "waste.disposition.set",
    "waste",
    "Set a pending waste event's disposition (POST /v1/waste/events/:id/disposition); transitions out of a non-pending disposition are refused 409 (spec 05 §7)",
  ),
  perm(
    "waste.cost.manual",
    "waste",
    "Supply a manual unit cost on a waste event (§3.2 rung 4) — audit-logged; without this code unit_cost_minor is server-resolved only (spec 05 §7)",
  ),
  perm(
    "waste.remnant.manage",
    "waste",
    "Register, update, reserve, release, re-class and scrap remnants (spec 05 §7; per-action role narrowings service-enforced)",
  ),
  // production (module 04) — spec 04 §7. SLICE 04-A registers the TEN codes it
  // enforces: the WO lifecycle, the office-recorded operation result, the
  // office-side material issue, the cost ledger and its redaction gate.
  //
  // WHY TEN AND NOT THE TWO §7'S PROSE NAMES. §7 opens by naming two codes the
  // module "registers" (`production.view_costs`,
  // `production.approve_shift_handoff`) and then prints a TWENTY-ROW action
  // matrix whose role columns differ row by row: Close is
  // group_admin/facility_admin/finance, Complete is
  // group_admin/facility_admin/planner, Cancel-in-progress is
  // group_admin/facility_admin, Manual cost adjustment is finance alone. Two
  // codes cannot express that, and §5 row 34 ("the same grant as Skip;
  // registers no new permission code") only parses if Skip HAS a code. These
  // ten are that matrix — one code per distinct role set, on the platform
  // grammar, shaped like module 06's `inventory.receipt.create`. Spec 04 §7 is
  // amended in the same PR to list them (the spec is the contract).
  //
  // `production.approve_shift_handoff` is NOT here: 04-C enforces the defect
  // hand-off disposition, so until then it stays in
  // `packForwardDeclaredPermissionCodes` below, where the cable pack's
  // `shift_boss` role forward-references it. A catalog code no route checks is
  // a grant `GET /v1/permissions` offers to a role that cannot use it — the
  // rule slice 05-A wrote. The remaining §7 actions (floor events, defect
  // flagging, scoreboards, drift, conflicts, downtime/shift/device management,
  // piece rate) register with 04-B and 04-C for the same reason.
  //
  // `production.view_costs` is NOT a pure field grant: it gates whole endpoints
  // (§5 rows 11 and 12) AND drives the §7 field-level redaction, exactly as
  // `catalogue.cost.read` does. Note the redaction SHAPE differs from module
  // 06's null+`redacted:true`: spec 04 §7 says "stripped", and M04-S12/M04-S15
  // assert at the API response that a qc session's payload contains no
  // `*_minor` KEY AT ALL.
  //
  // Cell-level narrowings the static guard cannot express — sales' own-book MTO
  // rows, floor_worker's own-station operations, planner's draft/released-only
  // cancel — are service-enforced on top of these codes, the D-040 two-layer
  // pattern. Descriptions are seeded verbatim by migration
  // 0019_production_work_orders.sql (parity gate, roles.int.test.ts).
  perm(
    "production.read",
    "production",
    "View work orders, operations, material lines + the station queue and traveler (sales own-book MTO only, floor_worker own-station ops only — service-enforced) (spec 04 §7)",
  ),
  perm(
    "production.wo.write",
    "production",
    "Create work orders + edit drafts (priority stays editable through released) (spec 04 §7)",
  ),
  perm(
    "production.wo.release",
    "production",
    "Release work orders — ATP check + one module-06 reservation per component line (spec 04 §7)",
  ),
  perm(
    "production.wo.complete",
    "production",
    "Complete work orders — output receipt, co/by-product apportionment + NRV credit (spec 04 §7)",
  ),
  perm(
    "production.wo.close",
    "production",
    "Close work orders — freeze the four cost accumulators, refuse further mutation (spec 04 §7)",
  ),
  perm(
    "production.wo.cancel",
    "production",
    "Cancel work orders with a reason (planner limited to draft/released — service-enforced) (spec 04 §7)",
  ),
  perm(
    "production.operation.record",
    "production",
    "Skip an operation + record an operation result from the office paper traveler (spec 04 §7)",
  ),
  perm(
    "production.material.issue",
    "production",
    "Issue material against a work order + return it to stock (spec 04 §7)",
  ),
  perm(
    "production.view_costs",
    "production",
    "See the four WO cost accumulators, the cost ledger and every *_minor field on production payloads (spec 04 §7 field redaction)",
  ),
  perm(
    "production.cost.adjust",
    "production",
    "Post a manual cost adjustment against a work order, reason required + audit-chained (spec 04 §7)",
  ),
  // Sub-step 04-B-1 (shift-calendar spine): ONE code for §7's "Manage downtime
  // reasons / shift defs / devices" row (group_admin + facility_admin),
  // registered with the first route it gates — e24, the shift-definition CRUD.
  // 04-B-2's device registry (e26) and 04-C's downtime-reason CRUD reuse it;
  // registering three codes for one role set would invert the D-074 rule (one
  // code per DISTINCT role set). Description seeded verbatim by migration
  // 0021_production_shift_calendar.sql (parity gate, roles.int.test.ts).
  perm(
    "production.floor.manage",
    "production",
    "Manage shift definitions, floor devices and downtime reasons — the floor calendar and registry surfaces (spec 04 §7)",
  ),
  // Sub-step 04-B-2 (floor event spine): the THREE codes its endpoints
  // enforce, one per distinct §7 role set (D-074's rule), registered with the
  // routes that first check them. e26 (device registry) reuses
  // `production.floor.manage` above — same role set, same row. floor_worker's
  // grant of `production.floor.record` is narrowed to its OWN department's
  // stations at the service layer (D-040 two-layer), and the §7 footnote-ᴬ
  // grant of `production.material.issue` to floor_worker lands in migration
  // 0022 alongside e13's scan-issue path — the endpoint that justifies it
  // (0019 deliberately withheld it; see the migration's grants block).
  // Descriptions seeded verbatim by 0022_production_floor_spine.sql (parity
  // gate, roles.int.test.ts).
  perm(
    "production.floor.record",
    "production",
    "Record floor execution events through the device batch — start/pause/resume/complete, quantity reports and material scans (spec 04 §7)",
  ),
  perm(
    "production.conflict.resolve",
    "production",
    "Resolve floor sync conflicts — apply with edit, discard or acknowledge; originals preserved, projections corrected only by compensating events (spec 04 §7)",
  ),
  perm(
    "production.labor.adjust",
    "production",
    "Post manual labor time entries and corrections against an operation, reason required + audit-chained (spec 04 §7)",
  ),
  // Sub-step 04-C-1 (downtime truth): ONE code for the §7 telemetry-read role
  // set — "View drift charts" (ga/fa/planner/maintenance) and the e22 downtime
  // list carry the SAME set, so per D-074's rule they share a code; e20/e21
  // (drift series + barrel records) reuse it when 04-C-2 mounts them.
  // Registered with its first route (e22). Description seeded verbatim by
  // migration 0024_production_downtime.sql (parity gate, roles.int.test.ts).
  perm(
    "production.telemetry.read",
    "production",
    "Read floor telemetry — downtime logs (e22) and, from slice 04-C-2, calibration drift series and barrel records (e20/e21); one code because §7 gives the two reads one role set (spec 04 §7)",
  ),
  // Sub-step 04-C-4 (scoreboard + piece rate): TWO codes, one per DISTINCT §7
  // role set (D-074's grammar).
  //
  // `production.scoreboard.read` — §7's "View scoreboard (office, with all
  // shifts)" row is group_admin / facility_admin / planner / finance / qc /
  // maintenance, and its floor_worker cell reads "wall-mode/floor variant".
  // That set is NOT `production.telemetry.read`'s (no finance, no qc there), so
  // it cannot reuse it. The floor_worker cell is the SAME capability over the
  // SAME columns — §2.10 gives the scoreboard no cost columns at all, so there
  // is nothing to redact — narrowed to the principal's own facilities by the
  // RLS fence; one code with a scope narrowing (D-040 two-layer), never a
  // second code. e16 (the worker's own tally) and the S12 wall board ride it
  // too: same rows, fewer of them.
  //
  // `production.piece_rate.read` — §7 prints TWO piece-rate rows ("View
  // piece-rate qty" and "View piece-rate rate_minor/amount_minor") with
  // IDENTICAL role columns, so by the one-code-per-distinct-role-set rule they
  // are one code. The qty/money split between them is a FIELD decision, not a
  // capability: money is present when the principal holds
  // `production.view_costs` or the row is its own (§2.13's "visible only to
  // self, facility_admin, finance, group_admin"), and ABSENT — no key at all,
  // §7's redaction shape — otherwise.
  //
  // Descriptions seeded verbatim by migration
  // 0028_production_scoreboard_piece_rate.sql (parity gate, roles.int.test.ts).
  perm(
    "production.scoreboard.read",
    "production",
    "Read the shift scoreboard — the (shift, work centre) attribution rows and facility totals (e17) and the S12 wall board; floor principals see their own facilities only (spec 04 §7)",
  ),
  perm(
    "production.piece_rate.read",
    "production",
    "Read piece-rate capture entries (e27); rate_minor/amount_minor are absent unless the principal holds production.view_costs or the row is its own (spec 04 §7)",
  ),
  // Sub-step 04-C-5 (defect hand-offs + the qc hold): TWO codes, and the second
  // is a deliberate NON-reuse.
  //
  // `production.approve_shift_handoff` — the code §7's PROSE has named since
  // 04-A and the one the cable pack's `role.shift_boss` forward-references
  // (spec 02 §2.12). It has sat in `packForwardDeclaredPermissionCodes` below
  // precisely so the DB↔catalog parity gate stayed green while no route checked
  // it; 04-C-5 mounts e18 (the disposition queue) and e19 (the disposition), so
  // the code enters the catalog here and LEAVES that list in the same change —
  // the contract that list's doc comment writes down, executed. The test below
  // fails on any overlap, so it cannot rot into a duplicate.
  //
  // `production.qc_hold.release` — §5 e32. It carries the SAME §7 role set
  // (group_admin / facility_admin / qc) as the code above and is still a second
  // code, because D-074's rule is one code per DISTINCT role set, not one code
  // per role set: `production.floor.manage` and `production.conflict.resolve`
  // already share a set and stay separate. Here the separation is load-bearing.
  // `approve_shift_handoff` is grantable to a custom role BY NAME and a shipped
  // pack already does it; "a shift boss may decide what happens to a defective
  // drum" and "a shift boss may clear a quality hold a failed inspection put on
  // an operation" are different sentences, and folding them would silently
  // widen every pack that names the first.
  //
  // Descriptions seeded verbatim by migration
  // 0029_production_defect_handoffs.sql (parity gate, roles.int.test.ts).
  perm(
    "production.approve_shift_handoff",
    "production",
    "Read the defect hand-off queue (e18) and dispose a hand-off rework/scrap/accepted_as_is (e19) — the disposition retro-attributes the defect to the shift that PRODUCED it (spec 04 §7)",
  ),
  perm(
    "production.qc_hold.release",
    "production",
    "Clear a quality hold on an operation (e32), note required and audit-chained; module 07 sets the hold, this releases it manually (spec 04 §7)",
  ),
  // quality (module 07) — spec 07 §7. SLICE 07-A registered the SEVEN codes its
  // §5 routes carry; slice 07-B adds its FIVE (seeded together — see below).
  //
  // THE WHOLE §7 SET, ENUMERATED ONCE SO THE NEXT SLICE DOES NOT RE-DERIVE IT.
  // §7 prints an eighteen-row action matrix whose role columns differ row by
  // row, and D-074's rule is one code per DISTINCT role set. These are the
  // SEVENTEEN codes that matrix plus §5's holds-read row imply, with the slice
  // that ENFORCES each:
  //
  //   07-A · quality.catalog.read        ga/fa/qc/planner/procurement/storekeeper/floor_worker
  //   07-A · quality.catalog.manage      ga/fa/qc  (control points + failure causes: ONE row set)
  //   07-A · quality.vertical_pack.seed  ga alone
  //   07-A · quality.inspection.read     every role except maintenance
  //   07-A · quality.inspection.create   ga/fa/qc/storekeeper/floor_worker
  //   07-A · quality.inspection.record   fa/qc/floor_worker (+ ga by the platform invariant)
  //   07-A · quality.inspection.cancel   ga/fa/qc
  //   07-B · quality.resolution.rework   fa/qc/planner (+ ga)      — §7 "Resolve: rework"
  //   07-B · quality.resolution.dispose  qc (+ ga)                 — §7 "Resolve: scrap / accept_deviation"
  //   07-B · quality.hold.place          fa/qc/storekeeper (+ ga)  — §7 "Holds: place manual"
  //   07-B · quality.hold.disposition    qc/procurement (+ ga)     — §7 "Holds: disposition"
  //   07-B · quality.hold.read           fa/planner/sales/procurement/finance/qc (+ ga)
  //                                      — §5's holds list/detail (07-B decision D8: that role
  //                                      set matches NO existing code, and D-074's rule is one
  //                                      code per DISTINCT role set)
  //   07-C · quality.cut.execute         fa/qc/floor_worker (+ ga) — §7 "Guided cut"
  //   07-E · quality.coa.draft           fa/qc/sales (+ ga)
  //   07-E · quality.coa.issue           qc (+ ga)                 — issue AND revoke
  //   07-E · quality.coa_template.manage ga/fa/qc
  //   07-E · quality.analytics.read      ga/fa/qc/planner/finance + procurement (supplier panel)
  //                                      + floor_worker (shift scoreboard only)
  //
  // ALL SEVENTEEN ARE REGISTERED HERE — the seven 07-A codes, the five 07-B
  // codes, 07-C's `quality.cut.execute` (seeded by migration 0039 with its
  // five §5 cut routes), 07-E's CoA three (migration 0045 with their seven
  // routes) and `quality.analytics.read` last (migration 0046 with the five
  // analytics routes).
  // `quality.hold.read` and `quality.hold.place` carry
  // 07-B-1's holds routes (list, manual place, detail); the two resolutions
  // and `quality.hold.disposition` were seeded in the same migration wave
  // (0036) and their routes are MOUNTED by 07-B-2: `quality.hold.disposition`
  // on `POST /holds/{id}/disposition`, `quality.resolution.rework` on
  // `POST /inspections/{id}/resolve` — with `quality.resolution.dispose`
  // additionally required for kind scrap|accept_deviation, service-enforced
  // (D-040 two-layer; one code per route is the register's invariant, and no
  // principal is stranded by it: every role holding dispose also holds
  // rework). The LAST one —
  // `quality.analytics.read` — landed with the five analytics routes
  // (migration 0046), closing the seventeen: a catalog code no route checks
  // is a grant `GET /v1/permissions` offers to a role that cannot use it
  // (the rule slice 05-A wrote and 04-A restated), and none is in that state. 07-C's
  // `quality.cut.execute` obeys the same rule and is here BECAUSE this change
  // registers its five routes: the code and the surface land together.
  //
  // NO FIELD GRANT — AND THE MONEY QUESTION IS ADJUDICATED (D-126, owner,
  // 2026-08-17, closing D-123 ruling 10 / seam C10). The rule taken: THE
  // ORIGINATING MODULE GOVERNS ITS OWN NUMBER. `value_at_risk_minor` is this
  // module's number about this module's subject, so spec 07 §7's set rules it
  // — group_admin / facility_admin / finance / qc / planner — ROLE-keyed in
  // the serializer (the module-05 shape); still no field-grant code, and
  // module 05's shipped `WASTE_COST_ROLES` is UNCHANGED: a module-05 valuation
  // relayed through a resolve response keeps module 05's narrower set.
  // Redaction is ABSENCE of BOTH `value_at_risk_minor` AND `currency`
  // together (spec 07 §7 literal — a deliberate, documented divergence from
  // module 05's always-present `currency`).
  //
  // Cell-level narrowings the static guard cannot express — floor_worker's
  // assigned stage on the library read, sales' final-stage-only and
  // procurement's receiving-only inspection reads, floor_worker's own facility
  // — are service-enforced on top of these codes (D-040's two-layer pattern).
  // `quality.inspection.cancel` carries the SAME role set as
  // `quality.catalog.manage` and is deliberately a second code: D-074's rule is
  // one code per DISTINCT role set, not one per role set (0029's precedent),
  // and a pack that grants a custom qc-clerk role the control-point library
  // must not silently acquire the authority to cancel a check somebody is
  // executing. Descriptions are seeded verbatim by migration
  // 0034_quality_control_points_inspections.sql (parity gate, roles.int.test.ts).
  perm(
    "quality.catalog.read",
    "quality",
    "View quality control points with their bindings and check items (floor_worker sees its assigned stage — service-enforced) (spec 07 §7)",
  ),
  perm(
    "quality.catalog.manage",
    "quality",
    "Create, version and deactivate control points, and manage the failure-cause catalog — one code because spec 07 §7 gives both rows the same role set (spec 07 §7)",
  ),
  perm(
    "quality.vertical_pack.seed",
    "quality",
    "Seed the vertical pack's control points and failure causes into this tenant (group_admin only; idempotent per code, tenant rows are never overwritten) (spec 07 §7)",
  ),
  perm(
    "quality.inspection.read",
    "quality",
    "View the inspection queue, inspection detail and the ship-readiness gate (sales final-stage only, procurement receiving-only, floor_worker own facility — service-enforced) (spec 07 §7)",
  ),
  perm(
    "quality.inspection.create",
    "quality",
    "Create an ad-hoc inspection from an active control point against a visible subject (spec 07 §7)",
  ),
  perm(
    "quality.inspection.record",
    "quality",
    "Record inspection results and complete an inspection pass/fail — the physical act on the floor or its office fallback (spec 07 §7)",
  ),
  perm(
    "quality.inspection.cancel",
    "quality",
    "Cancel an open or in-progress inspection with a reason (subject scrapped elsewhere, duplicate) (spec 07 §7)",
  ),
  // Slice 07-B's five (the enumeration above). Descriptions are seeded
  // VERBATIM by the 07-B migration — byte-identical strings, compared by the
  // roles.int.test.ts parity gate.
  perm(
    "quality.resolution.rework",
    "quality",
    "Resolve a failed inspection as rework — re-opens the producing operation through module 04's seam; refused on a HELD operation for a principal without production.qc_hold.release — service-enforced (spec 07 §3.5a, §7)",
  ),
  perm(
    "quality.resolution.dispose",
    "quality",
    "Resolve a failed inspection as scrap or accept-deviation — scrap posts through module 05's in-txn door and every path clears the operation gate (spec 07 §3.5b/e, §7)",
  ),
  perm(
    "quality.hold.place",
    "quality",
    "Place a manual quarantine hold on a stock unit or lot — the stock is quarantined through module 06 in the same transaction (spec 07 §2.7, §7)",
  ),
  perm(
    "quality.hold.disposition",
    "quality",
    "Release or dispose a quarantine hold (release/rework/scrap/downgrade/return_to_supplier); procurement may only return_to_supplier — service-enforced (spec 07 §3.5c, §7)",
  ),
  perm(
    "quality.hold.read",
    "quality",
    "View the quarantine board, hold list and hold detail — value_at_risk_minor redacted per §7 (spec 07 §5, §7)",
  ),
  // Slice 07-C's ONE (the enumeration above) — §7's "Guided cut" row, on all
  // FIVE §5 cut routes including the detail read (D-163: one code per DISTINCT
  // role set, one code per route, and cancel narrowed SERVICE-side to
  // {group_admin, facility_admin, qc} rather than minting a second code for one
  // route's narrower set). The description is seeded VERBATIM by migration
  // 0039_quality_guided_cut.sql — byte-identical, compared by the
  // roles.int.test.ts parity gate.
  perm(
    "quality.cut.execute",
    "quality",
    "Propose, confirm, execute and cancel a guided cut on a failed length-based inspection, and read a cut event with its segments — cancel is narrowed to group_admin, facility_admin and qc service-side (spec 07 §3.5d, §5, §7)",
  ),
  // Slice 07-E's CoA three (the enumeration above) — seeded VERBATIM by
  // migration 0045_quality_coa_surface.sql, byte-identical strings compared by
  // the roles.int.test.ts parity gate. `quality.coa.draft` carries every read
  // a drafter needs (templates list, documents list, draft, preview) because
  // §5's drafter cell — qc/sales/facility_admin — is ONE role set and D-074's
  // rule is one code per distinct set; `quality.coa.issue` carries issue AND
  // revoke, because attestation and its withdrawal are one authority.
  // `quality.analytics.read` (07-E's fourth) lands with the analytics
  // endpoints, not here — a catalog code no route checks is a grant offered
  // to a role that cannot use it.
  perm(
    "quality.coa.draft",
    "quality",
    "Draft certificates of analysis/conformance from a lot, list certificates and templates, and render draft previews (spec 07 §3.6, §5, §7)",
  ),
  perm(
    "quality.coa.issue",
    "quality",
    "Issue a drafted certificate (freeze PDF, mint the public link) and revoke an issued one with reason, optionally drafting the next revision (spec 07 §3.6 steps 4–5, §7)",
  ),
  perm(
    "quality.coa_template.manage",
    "quality",
    "Create and update CoA templates — branding, signatory, product binding; issued certificates keep their frozen snapshot (spec 07 §2.10, §7)",
  ),
  // Slice 07-E's LAST one (the enumeration above, now complete at seventeen)
  // — seeded VERBATIM by migration 0046_quality_analytics.sql WITH the five
  // §5 analytics routes that check it. ONE code over five routes whose §5
  // role cells differ: D-074 would mint four codes for four reads of one
  // cube, so the per-route cells (floor_worker: scoreboard only;
  // procurement: supplier panel only; refresh: group_admin only — D-216-B)
  // are service-enforced on top (D-040's two layers, the D-163 shape).
  perm(
    "quality.analytics.read",
    "quality",
    "Read the quality analytics surface — FPY series, failure Pareto, shift scoreboard, supplier receiving rates — and the group_admin-only projection refresh; per-route §5 role cells service-enforced (spec 07 §2.12, §5, §7)",
  ),
  // sales (module 09) — spec 09 §7. SLICE P3-A registers the SEVEN codes its
  // §5 foundation routes carry (customers/contacts/channels, distributor
  // lists, price lists, off-cut classes, margin guards, settings, pricing
  // resolve). Seeded VERBATIM by migration 0050_sales_foundations.sql (parity
  // gate, roles.int.test.ts), which also carries the cell-by-cell §7
  // transcription.
  //
  // THE WHOLE §7 SET, ENUMERATED ONCE SO THE NEXT SLICE DOES NOT RE-DERIVE
  // IT. §7 names SEVENTEEN codes since P3-E2 PR E2-1 (D-312, 2026-09-27)
  // added `sales.opportunity.write` WITH the deal write routes, and ALL
  // SEVENTEEN are registered below. It was SIXTEEN from P3-E PR-E2 (D-292,
  // 2026-09-18), which added `sales.activity.write` WITH the task routes.
  // The fifteen before that were complete once P3-D
  // PR-D4 (D-288, 2026-09-17) landed `sales.order.override_status` WITH its
  // route; PR-D3 (D-286) had landed `sales.shipment.post` and
  // `sales.order.deliver` WITH the shipment routes; `sales.order.confirm`
  // is struck and counted in neither (D-258). The twelfth was P3-D-1's
  // `sales.order.write` (D-283), the direct order's own code. The slice that
  // ENFORCES each registers it (a catalog code no route checks is a grant
  // `GET /v1/permissions` offers to a role that cannot use it — the 05-A
  // rule):
  //
  //   P3-A · sales.read             ga/fa/planner/sales/finance/qc/storekeeper
  //   P3-A · sales.manage_customers ga/fa/sales (assigned) + finance (credit
  //                                 fields only — §7's credit-edit cell rides
  //                                 the same PATCH route, service-narrowed)
  //   P3-A · sales.tier.manage      ga/fa (ga-only cells service-narrowed)
  //   P3-A · sales.margin.read      FIELD GRANT — ga/fa/finance (+ sales when
  //                                 tenant-granted; never by default)
  //   P3-A · sales.read_value       FIELD GRANT — ga/fa/finance/sales
  //   P3-A · sales.margin.approve   ga (all) / fa (≤ §3.4 threshold) — D-231
  //   P3-A · sales.credit.approve   ga/fa/finance — D-231
  //   P3-C-1 · sales.quote.write, sales.quote.send, sales.order.cancel
  //                                 (0055) — quotes and orders shipped as ONE
  //                                 slice. `sales.order.confirm` is NOT among
  //                                 them and no later slice owes it:
  //                                 accept-manual rides `sales.quote.send`, no
  //                                 route reads confirm, §7 struck it (D-258).
  //                                 Until 2026-09-10 these two lines still
  //                                 forecast P3-B/P3-C and were contradicted
  //                                 only by the P3-C-1 note sixty lines below,
  //                                 which is not where a reader checking the
  //                                 roll-call looks.
  //   P3-C-2 · sales.override.write ga/fa/sales — §7's "Customer price override
  //                                 create" row (D-263: ONE code for create AND
  //                                 revoke; revoke service-narrowed to fa/ga)
  //   P3-D-1 · sales.order.write    ga/fa/sales (assigned) — POST /v1/orders
  //                                 (D-283: the direct order; its decide rides
  //                                 sales.margin.approve, no new code)
  //   P3-D PR-D3 · sales.shipment.post
  //                                 ga/fa/storekeeper (own facility) — open /
  //                                 pick / undo / pack / ship / cancel (0063;
  //                                 cancel narrowed to fa/ga service-side,
  //                                 D-286)
  //   P3-D PR-D3 · sales.order.deliver
  //                                 ga/fa/sales (assigned)/storekeeper (own
  //                                 facility) — POST /v1/orders/{id}/
  //                                 mark-delivered (0063; a SECOND code by
  //                                 D-074's distinct-role-set test, D-286)
  //   P3-D PR-D4 · sales.order.override_status
  //                                 ga/fa — POST /v1/orders/{id}/override-status
  //                                 (0064; §3.6's audited manual transition; a
  //                                 new code because no existing sales code
  //                                 carries exactly {ga, fa}, D-288)
  //   P3-E PR-E2 · sales.activity.write
  //                                 ga/fa/sales — POST /v1/sales-activities and
  //                                 POST …/{id}/complete (0067; the manager's
  //                                 tasks, D-292 ruling 2 — a SIXTEENTH code
  //                                 §7 gains with activities step 1: the role
  //                                 set equals `sales.quote.write`'s, but a
  //                                 task is a different policy surface, the
  //                                 D-283 ruling 1 test)
  //   P3-E2 PR E2-1 · sales.opportunity.write
  //                                 ga/fa/sales — the deal writes: create,
  //                                 edit, move, win, lose, reopen and the
  //                                 quote link (0073; D-312 ruling 15 — a
  //                                 SEVENTEENTH code, the activity code's role
  //                                 set on a different policy surface; stage
  //                                 tuning rides sales.tier.manage)
  //
  // margin.approve / credit.approve are P3-A rows WITHOUT a mounted decide
  // route, by the P3-0 adjudication (D-231): they are the review seam's
  // permission fabric, and every P3-A serializer's §7 role math is defined
  // against who HOLDS them — registering them later would put the serializer
  // and the catalog out of step for the length of the slice. Their decide
  // routes mount in P3-B/P3-C and change no grant.
  perm(
    "sales.read",
    "sales",
    "View customers, contacts, price lists, off-cut discount classes, sales settings and the pricing dry-run (sales narrowed to assigned customers, storekeeper to ship-relevant reads — service-enforced) (spec 09 §7)",
  ),
  perm(
    "sales.manage_customers",
    "sales",
    "Create and edit customers, contacts and messaging channels (sales: assigned customers, profile fields only; finance: credit limit/terms/hold fields only; archive and channel removal: admins — service-enforced, D-040 two-layer) (spec 09 §7)",
  ),
  perm(
    "sales.tier.manage",
    "sales",
    "Sales tier governance — customer tier changes, distributor eligible lists, price lists and their items, off-cut discount classes, margin guards and tenant sales settings (activation and guard/class edits group_admin-only — service-enforced) (spec 09 §7)",
  ),
  perm(
    "sales.margin.read",
    "sales",
    "See unit_cost_minor, margin_pct and margin_guard_pct on pricing resolves, quote/order lines and price-list margin previews — field grant; without it only the boolean breach flag renders (spec 09 §7)",
    { field: true },
  ),
  perm(
    "sales.read_value",
    "sales",
    "See order/quote money totals, open AR and analytics order values — field grant (spec 09 §7)",
    { field: true },
  ),
  perm(
    "sales.margin.approve",
    "sales",
    "Decide pending margin-breach reviews on quotes, direct orders and price overrides (facility_admin within the §3.4 thresholds, group_admin above — service-enforced; in-module until module 12 absorbs the surface, D-231) (spec 09 §7)",
  ),
  perm(
    "sales.credit.approve",
    "sales",
    "Release or uphold a blocked_* credit hold on an order, including recording an out-of-band prepayment (in-module until module 12 absorbs the surface, D-231) (spec 09 §7)",
  ),
  // sales (spec 09 §7, slice P3-C-1) — the THREE document codes seeded
  // VERBATIM by migration 0055_sales_quotes.sql WITH the §5 routes that check
  // them (the pairing rule; parity gate, roles.int.test.ts). Descriptions here
  // and in that migration's INSERT are byte-identical by construction — the
  // parity test compares them as strings.
  //
  // §7's "Quote create/edit/send" row splits into TWO codes because §5
  // separates authoring from the customer-facing dispatch and §8 gives them
  // different autonomy defaults (draft `autonomous`, send `threshold`). That
  // is D-074-legal: the rule forbids a REDUNDANT code, and these two have
  // different policy surfaces — a tenant letting juniors draft but not send
  // has no other way to say it.
  //
  // `sales.order.confirm` IS NOT REGISTERED, here or anywhere: manual
  // acceptance mounts at `POST /v1/quotes/{id}/accept-manual` under
  // `sales.quote.send` (the dispatch half of the quote lifecycle), so no route
  // checks it, and §7 struck it rather than reserve it (spec 09 §7, D-258 —
  // P3-D's direct order confirms through a decide action riding
  // `sales.margin.approve`). The enumeration above forecast it for a "P3-C"
  // order slice until 2026-09-10 and now carries the strike itself, so this
  // paragraph corroborates the roll-call instead of being the only place that
  // contradicts it. The direct-order CREATE, when it landed (P3-D-1, D-283),
  // took a code of its own — `sales.order.write`, at the end of this block —
  // and not `sales.order.confirm`: the struck name described a confirmation
  // verb, and the direct order confirms through its decide.
  perm(
    "sales.quote.write",
    "sales",
    "Create, revise, duplicate, cancel and record rejection on quotes (sales narrowed to assigned customers and to quotes they own or collaborate on — service-enforced; agent principals per the §8 autonomy policy) (spec 09 §7)",
  ),
  perm(
    "sales.quote.send",
    "sales",
    "Send a quote to the customer, remind on a sent quote, force-expire it and record a manual acceptance — the customer-facing dispatch, held apart from authoring so a tenant can let juniors draft without sending (spec 09 §7)",
  ),
  perm(
    "sales.order.cancel",
    "sales",
    "Cancel a sales order and release its inventory reservations (sales narrowed to their own confirmed orders — service-enforced; refused while active work orders exist, 409 ORDER_HAS_ACTIVE_WOS) (spec 09 §7)",
  ),
  // sales (spec 09 §7, slice P3-D-1 — D-283, 2026-09-15) — the DIRECT ORDER's
  // code, seeded VERBATIM by migration 0061_sales_order_line_pricing_facts.sql
  // WITH the one §5 route that checks it, `POST /v1/orders` (the pairing rule;
  // parity gate, roles.int.test.ts). §7's own sentence asked for exactly this
  // ("whatever code P3-D's `POST /v1/orders` checks registers WITH that
  // route"), and 0055's "WHY THREE AND NOT FOUR" forecast it.
  //
  // A NEW code and not a ride on `sales.quote.send`, on D-074's own test —
  // the rule forbids a REDUNDANT code, and these two are different policy
  // surfaces even though their default §7 cells coincide: accept-manual
  // (under `sales.quote.send`) commits the tenant to an offer a customer was
  // SENT and said yes to; a direct order commits it to a document no customer
  // ever saw. A tenant that lets juniors quote, send and record a phone
  // acceptance but not place an unquoted order has no other way to say so —
  // the same shape as write-vs-send above. `sales.quote.send`'s seeded
  // description names "the customer-facing dispatch", which a direct order is
  // not, and 0001–0060 hold no precedent for rewriting a seeded description.
  // The agent question does not separate the two (an agent authorizes through
  // its service role's lineage — 0055's §7 note — and role 0004 `sales` holds
  // both), so it is not argued.
  //
  // The decide (`POST /v1/orders/{id}/decide`) rides `sales.margin.approve`,
  // whose description above already names direct orders; no second code.
  perm(
    "sales.order.write",
    "sales",
    "Place a direct sales order without a quote — every line priced through §3.2 at creation; confirmed at once when every line is on-list and clears its guard, otherwise held in pending_approval for a sales.margin.approve decision (sales narrowed to assigned customers — service-enforced, D-224) (spec 09 §7, D-283)",
  ),
  // sales (spec 09 §7, slice P3-C-2 — D-263, 2026-09-08) — ONE code for the
  // §2.9 override surface's two writes, seeded VERBATIM by the P3-C-2
  // migration WITH the §5 routes that check it (`POST /v1/customer-price-
  // overrides` and `POST …/{id}/revoke`; the pairing rule; parity gate,
  // roles.int.test.ts). §7's "Customer price override create" row is
  // ga ✓ / fa ✓ / sales ✓ (auto-routes to approval on breach); revoke is
  // fa/ga only and is a SERVICE narrowing on this same code — D-040's two
  // layers, the `sales.tier.manage` ga-only-cells shape. A second code for
  // revoke was rejected because its only holders already hold the first
  // (D-074's redundancy rule). The list read rides `sales.read` with the
  // price fields under `sales.read_value`; the decide rides
  // `sales.margin.approve` (D-231), unchanged.
  perm(
    "sales.override.write",
    "sales",
    "Propose a customer price override (sales narrowed to assigned customers; a margin breach routes it to approval) and revoke one (facility_admin / group_admin only — service-enforced, D-040 two-layer) (spec 09 §7, D-263)",
  ),
  // sales (spec 09 §7, slice P3-D PR-D3 — D-286, 2026-09-17) — the SHIPMENT
  // surface's TWO codes, seeded VERBATIM by migration 0063_sales_shipments.sql
  // WITH the §5 routes that check them (the pairing rule; parity gate,
  // roles.int.test.ts). TWO and not one, on D-074's own test — one code per
  // DISTINCT role set:
  //   * `sales.shipment.post` — §7's "Shipment pick/pack/ship" row: ga ✓,
  //     fa ✓, storekeeper ✓ (own facility). Gates open / pick / undo / pack /
  //     ship / cancel; the storekeeper's facility arm and the cancel row's
  //     fa/ga cell are SERVICE narrowings on this same code (D-040 two-layer
  //     — the `sales.override.write` revoke shape; a second code for a subset
  //     of the first's holders is D-074's redundancy).
  //   * `sales.order.deliver` — §5's `mark-delivered` cell: storekeeper /
  //     sales / facility_admin, plus group_admin by the platform invariant
  //     (spec 16 §2.8). The role set DIFFERS from the first (sales is in it),
  //     so it is its own code; sales is narrowed to assigned customers and
  //     the storekeeper to their own facility service-side (D-224).
  // The customer's own `confirm-received` door carries no code — a token
  // door (§4.10), exactly as the quote accept is.
  perm(
    "sales.shipment.post",
    "sales",
    "Open, pick, undo, pack, ship and cancel a shipment against a confirmed order at the caller's facility — the §3.7 floor path; storekeeper narrowed to their own facility service-side, cancel narrowed to facility_admin/group_admin (spec 09 §7, D-286)",
  ),
  perm(
    "sales.order.deliver",
    "sales",
    "Mark a shipped order delivered on the carrier's word (spec 09 §3.6 shipped → delivered; sales narrowed to assigned customers, storekeeper to their own facility — service-enforced, D-224) (spec 09 §7, D-286)",
  ),
  // sales (spec 09 §7, slice P3-D PR-D4 — D-288, 2026-09-17) — §3.6's manual
  // transition, seeded VERBATIM by migration 0064_sales_documents.sql WITH
  // `POST /v1/orders/{id}/override-status` (the pairing rule). §5's cell is
  // facility_admin, plus group_admin by the platform invariant. No existing
  // sales code carries exactly {ga, fa}: riding `sales.order.cancel` would
  // hand every sales principal a forward status override, which §3.6 and §7
  // withhold from them (D-074's distinct-role-set test). The facility arm
  // (D-286 ruling 25) is a service narrowing on this code. The three PDF
  // doors mint NO code: they are reads under `sales.read`, with
  // `sales.read_value` checked in the service for the two priced documents.
  perm(
    "sales.order.override_status",
    "sales",
    "Force a sales order forward along the spec 09 §3.6 state machine with a mandatory reason: to ready, to shipped when goods have shipped and no shipment is open, or from delivered to closed; never to in production, delivered or cancelled, and never a ledger, line or shipment change (spec 09 §7, D-288)",
  ),
  // sales (spec 09 §7, P3-E PR-E2 — D-292, 2026-09-18) — activities step 1,
  // the manager's TASKS, seeded VERBATIM by migration
  // 0067_sales_activities_and_engine.sql WITH the two §5 routes that check it
  // (`POST /v1/sales-activities`, `POST /v1/sales-activities/{id}/complete`;
  // the pairing rule; parity gate, roles.int.test.ts). Role set {ga, fa,
  // sales} — equal to `sales.quote.write`'s, and a NEW code all the same by
  // D-283 ruling 1's test: D-074 forbids a REDUNDANT code, and writing a
  // follow-up task is a different policy surface from authoring a quote (a
  // tenant that lets a junior log calls without quoting has no other way to
  // say so). The narrowings are SERVICE-side (D-040 two-layer): a sales
  // principal creates only for themselves, and the assignee or an admin
  // completes. The list read rides `sales.read`, narrowed; the engine's
  // system tasks are written and closed with the system actor and check no
  // code.
  perm(
    "sales.activity.write",
    "sales",
    "Create manual sales tasks (call, meeting, to-do) and complete open ones, the engine's system tasks included (sales create only for themselves; the assignee or an admin completes — service-enforced, D-040 two-layer) (spec 09 §7, D-292)",
  ),
  // sales (spec 09 §7, P3-E2 PR E2-1 — D-312 ruling 15, 2026-09-27) — the
  // DEAL writes, seeded VERBATIM by migration 0073_sales_pipeline.sql WITH
  // the routes that check it: `POST /v1/opportunities`, `PATCH
  // /v1/opportunities/{id}`, `POST /v1/opportunities/{id}/move`, `…/win`,
  // `…/lose`, `…/reopen` and `POST /v1/quotes/{id}/opportunity` (the pairing
  // rule; parity gate, roles.int.test.ts). Role set {ga, fa, sales} — equal to
  // `sales.activity.write`'s, and a NEW code all the same by D-283 ruling 1's
  // test: moving a deal through the pipeline is a different policy surface
  // from writing a task or a quote. Narrowing 16 is SERVICE-side (D-040
  // two-layer): a sales principal creates only on an assigned customer and
  // writes only a deal it reads, and only a literal admin names another
  // owner. The deal READS ride `sales.read` under narrowing 15, and stage
  // tuning rides `sales.tier.manage`, so E2-1 mints this code and no other.
  perm(
    "sales.opportunity.write",
    "sales",
    "Create, edit, move, win, lose and reopen deals, and link a quote to a deal or unlink it (sales creates only on assigned customers and writes only the deals it reads; only an admin names another owner — service-enforced, D-040 two-layer) (spec 09 §7, D-312)",
  ),
  // sales (spec 09 §7, P3-J J-3 — D-464 ruling 2, D-469, 2026-10-18) — the
  // re-promise code, seeded VERBATIM by migration 0103_planning_promise_watch.sql
  // WITH `POST /v1/orders/{id}/re-promise` (spec 03 §5 row 79) and module
  // 03's withdraw of a committed or at-risk promise (row 76). Role set {ga,
  // fa, planner}, spec 09 §7's Re-promise row. Owner-visible: a committed
  // date is what the customer was told, so sales may still quote and commit
  // but neither changes nor withdraws it, and asks a planner (D-464 ruling
  // 2). No sales code carries exactly this set (D-074).
  perm(
    "sales.order.re_promise",
    "sales",
    "Re-promise an order line on a fresh quoted promise and withdraw a committed or at-risk promise; the customer is told only when the person turns it on (spec 09 §4.7, §5; spec 03 §5 rows 76 and 79; D-464 ruling 2, D-469)",
  ),
  // agents (module 14, P3-K K-2, D-513 ruling 1) — the five 14-A codes, seeded
  // VERBATIM by migration 0105_agents_core.sql WITH the /v1/agent* routes that
  // check them (the 0044/0045 pairing rule; parity gate, roles.int.test.ts).
  // Role sets are spec 14 §7.1's: read, manage and activity.read {ga, fa},
  // config.manage {ga}, invoke every seed role but floor_worker. The journal
  // has no code of its own (§7.2's checks decide each row). `agents.usage.read`
  // and `agents.usage.cost_read` register with 14-F's usage screen, their only
  // surface (D-513 ruling 2). A tenant clone made before 0105 keeps its codes.
  perm(
    "agents.read",
    "agents",
    "Read the Agent Hub, an agent's detail, its policies and the tool list (spec 14 §4.2, §4.3, §5, §7.1)",
  ),
  perm(
    "agents.invoke",
    "agents",
    "Manually trigger the agent of one's own domain and read its Hub card and runs read-only (spec 14 §5, §7.1; D-512 ruling 8)",
  ),
  perm(
    "agents.manage",
    "agents",
    "Enable or pause an agent, bind its memberships within the holder's own scope, set its tool policies within spec 14 §2.4's rules, cancel a run and trigger any agent in scope (spec 14 §7.1)",
  ),
  perm(
    "agents.config.manage",
    "agents",
    "Set an agent's caps and model profile, override and activate its prompt, edit the tenant's agent settings and loosen a tool past its current policy (spec 14 §7.1)",
  ),
  perm(
    "agents.activity.read",
    "agents",
    "Read the run list, a run's detail and the guardrail history (spec 14 §5, §7.1)",
  ),
  // messaging (module 17, slice P3-B) — the ten part-1 codes seeded VERBATIM
  // by migration 0052_messaging_core.sql plus part 2's two conversation-inbox
  // codes seeded by 0054_messaging_conversation_codes.sql WITH the §5
  // conversation routes that check them (the 0044/0045 pairing rule; parity
  // gate, roles.int.test.ts). D-074 one-code-per-DISTINCT-role-set; cell
  // narrowings service-side, D-040 two-layer.
  //
  // THE REST OF §7, ENUMERATED ONCE SO THE NEXT SLICE DOES NOT RE-DERIVE IT
  // (each registers with the slice that mounts its routes):
  //   unparking (D-232) · messaging.template.submit (Meta submit + sync —
  //           ga/fa only)
  //   admin console · account suspend is PLATFORM staff, never a tenant code
  //           (§7's last row).
  //
  // The §5 message-detail read (GET /v1/messaging/messages/{id}, roles
  // sales/fa/ga) mounts under messaging.conversation.manage: its role set is
  // exactly manage's distinct set, and D-074 forbids a second code for the
  // same set — recorded here so nobody re-derives a "messages read is
  // conversation.read" assumption from the §5 table's neighbouring rows.
  perm(
    "messaging.account.manage",
    "messaging",
    "Connect, disconnect and probe provider channel accounts (Telegram live; WhatsApp parked 422 WA_PARKED until the owner's BSP entry — D-232) (spec 17 §3.1, §7)",
  ),
  perm(
    "messaging.settings.read",
    "messaging",
    "View channel accounts with provider health, quotas and the webhook log (spec 17 §4.4, §7)",
  ),
  perm(
    "messaging.link.issue",
    "messaging",
    "Issue identity link codes — own user identity for every role; customer-contact codes narrowed to sales/facility_admin/group_admin service-side (D-040 two-layer) (spec 17 §3.2, §7)",
  ),
  perm(
    "messaging.identity.manage",
    "messaging",
    "List channel identities and update language/opt-out flags (clearing opted_out_hard is refused — only the person can re-opt-in by messaging START) (spec 17 §7)",
  ),
  perm(
    "messaging.identity.revoke",
    "messaging",
    "Revoke channel identities — own identity for every role; customer identities narrowed to sales/facility_admin/group_admin and other users' identities to admins, service-side (D-040 two-layer) (spec 17 §3.2, §7)",
  ),
  perm(
    "messaging.template.read",
    "messaging",
    "View the message template catalog with its per-provider/locale variant matrix (spec 17 §4.3, §7)",
  ),
  perm(
    "messaging.template.manage",
    "messaging",
    "Create and edit message templates and upsert their per-provider/locale variant bodies (system templates are undeletable and cannot be deactivated — SYSTEM_TEMPLATE_PROTECTED) (spec 17 §2.4, §7)",
  ),
  perm(
    "messaging.send",
    "messaging",
    "Send through the channel-abstracted entry — template render, §3.3 policy gates and provider resolution all server-side (spec 17 §3.3, §5, §7)",
  ),
  perm(
    "messaging.preferences.manage",
    "messaging",
    "Read and set channel preferences — own preference for every role; customer-contact and other-user preferences narrowed to sales/facility_admin/group_admin service-side (D-040 two-layer) (spec 17 §2.12, §7)",
  ),
  perm(
    "messaging.address.read",
    "messaging",
    "See full channel addresses (phone / chat id) on identities and conversation context — field grant; without it addresses render masked (+7•••••1234, chat:•••482) (spec 17 §7)",
    { field: true },
  ),
  // part 2 (0054) — the conversation-inbox pair, seeded with the §5
  // conversation routes that check them.
  perm(
    "messaging.conversation.read",
    "messaging",
    "View the conversation inbox, thread heads and thread messages — ga/fa/sales working the inbox; planner/finance/qc read-only (every write on the surface carries messaging.conversation.manage; addresses masked without messaging.address.read) (spec 17 §4.1, §5, §7)",
  ),
  perm(
    "messaging.conversation.manage",
    "messaging",
    "Work a conversation — reply free-text/media/template, assign, snooze, close, claim to a customer contact, mark read, mint media upload URLs, retry failed messages and read message delivery detail (the §5 message-detail role set equals this code's exact set — D-074) (spec 17 §4.1, §4.2, §5, §7)",
  ),
  // planning (spec 03 §7, D-351): the three P3-G codes migration 0087 seeds
  // with the master-data routes, and the two migration 0090 seeds with the
  // MPS routes (G-3).
  perm(
    "planning.read",
    "planning",
    "View planning settings, parameters, safety-stock policies, forecasts, the demand pool, refreshes and MPS plans (spec 03 §7, D-351)",
  ),
  perm(
    "planning.demand.manage",
    "planning",
    "Edit planning parameters, safety-stock policies, forecasts and their lines, and request a demand refresh (spec 03 §7, D-351)",
  ),
  perm(
    "planning.settings.manage",
    "planning",
    "Edit a facility's planning settings; changing either MPS approval bar needs the group_admin lineage, service-enforced (spec 03 §2.22, §7, D-351 ruling 8)",
  ),
  perm(
    "planning.mps.edit",
    "planning",
    "Override MPS cells, submit a plan for approval and propose a plan now (spec 03 §7, D-351)",
  ),
  perm(
    "planning.mps.approve",
    "planning",
    "Approve or reject a submitted MPS plan; approval is threshold-routed by the approver's lineage, service-enforced (spec 03 §3.2, §7, D-351)",
  ),
  // P3-H-1 (D-390 ruling 1): migration 0091 seeds the calendar pair with the
  // calendar routes and the preview (spec 03 §5 rows 26 to 36).
  perm(
    "planning.calendar.read",
    "planning",
    "View shift patterns, work-centre calendar assignments, calendar exceptions and the facility calendar preview (spec 03 §4.8, §7, D-390)",
  ),
  perm(
    "planning.calendar.manage",
    "planning",
    "Create and edit shift patterns, assign them to work centres, and add or remove calendar exceptions; a pattern used outside the caller's scope is fenced, service-enforced (spec 03 §2.12, §7, D-386 ruling 9, D-390)",
  ),
  // P3-H-2 (D-390 ruling 1): migration 0092 seeds the three MRP codes with the
  // run, planned-order and action-message routes (spec 03 §5 rows 38 to 49).
  perm(
    "planning.mrp.run",
    "planning",
    "Request an MRP run at a facility; the group-wide pass needs the code at group scope, service-enforced (spec 03 §3.3, §7, D-388, D-390)",
  ),
  perm(
    "planning.planned_order.read",
    "planning",
    "View planned orders, their explanations and pegging, the workbench counts and the MRP run history; a procurement lineage reads purchase orders only, service-enforced (spec 03 §4.5, §7, D-387 ruling 5, D-390)",
  ),
  perm(
    "planning.planned_order.manage",
    "planning",
    "Firm and cancel planned orders and accept or dismiss their action messages (spec 03 §3.3, §3.8, §7, D-389, D-390)",
  ),
  // P3-H-3 (D-390 ruling 1): migration 0094 seeds the conversion code with
  // `POST /v1/planned-orders/convert` (spec 03 §5 row 50).
  perm(
    "planning.planned_order.convert",
    "planning",
    "Convert firmed planned orders into draft work orders and draft transfers; a procurement lineage converts purchase orders only, service-enforced (spec 03 §3.4, §7, D-389 ruling 7, D-390)",
  ),
  // P3-I-2 (D-406 ruling 1): migration 0096 seeds the schedule pair with the
  // solve, schedule, run, capacity and overload routes (spec 03 §5 rows 56
  // to 66).
  perm(
    "planning.schedule.read",
    "planning",
    "View the schedule board, schedule versions and their diff, solve runs, the capacity load and the overloads; a planned order's number and a customer's name are narrowed per reader, service-enforced (spec 03 §4.6, §4.7, §7, D-406)",
  ),
  perm(
    "planning.schedule.publish",
    "planning",
    "Ask for a solve, and publish or discard a draft schedule at a facility (spec 03 §3.5, §7, D-399, D-406)",
  ),
  // P3-I-3 (D-406 ruling 1): migration 0097 seeds the move money's field
  // grant with the moves, the drag and the directives (spec 03 §5 rows 66
  // to 71).
  perm(
    "planning.cost.read",
    "planning",
    "See a proposed move's cost, value at risk and currency on a schedule's overloads — field grant; without it the moves are served in the same order with their money absent; applying an overtime move also needs it (spec 03 §3.5, §7, D-402, D-406, D-413)",
    { field: true },
  ),
  // P3-J J-1 (D-464 ruling 1): migration 0100 seeds the promise code with the
  // quote, commit, withdraw and read routes (spec 03 §5 rows 74 to 78).
  perm(
    "planning.promise.commit",
    "planning",
    "Quote a capable-to-promise date, commit a quoted promise to a sales order line, and withdraw a quoted promise; withdrawing a committed one needs sales.order.re_promise from J-3, service-enforced (spec 03 §3.7, §7, D-462, D-464)",
  ),
  // P3-J J-4 (spec 03 §7, D-464 ruling 3, D-472 ruling 3): the what-if
  // sandbox's two codes, registered with rows 80 to 85.
  perm(
    "planning.scenario.manage",
    "planning",
    "Create, solve, read and discard a what-if scenario and read its sandbox schedule and run; the cost impact needs planning.cost.read, service-enforced (spec 03 §3.9, §4.10, §7, D-471, D-472)",
  ),
  perm(
    "planning.scenario.promote",
    "planning",
    "Promote a solved scenario's persistable hypotheses to the live calendar, with planning.calendar.manage and planning.cost.read at the facility, service-enforced (spec 03 §3.9, §7, D-472 ruling 3)",
  ),
  perm(
    "sync.use_floor",
    "sync",
    "Use the floor PWA runtime: register a device (online), flush batches, heartbeat, fetch reference datasets, own sync tray (module 18)",
  ),
  perm("sync.view_fleet", "sync", "View the device fleet, batches, ledger (module 18)"),
  perm(
    "sync.manage_devices",
    "sync",
    "Rename/move/revoke/un-revoke devices, channel pins, force-advance seq, drift settings, dataset rebuild (module 18)",
  ),
  perm(
    "sync.resolve_drift",
    "sync",
    "View + resolve counter-drift events (money fields per field-grants) (module 18)",
  ),
  perm("platform.manage_tenants", "platform", "Tenant CRUD, modules, limits", { platform: true }),
  perm("platform.manage_plans", "platform", "Plan definitions", { platform: true }),
  perm("platform.view_health", "platform", "Fleet health board", { platform: true }),
  perm("platform.view_billing", "platform", "Usage + billing status", { platform: true }),
  perm("platform.impersonate_tenant", "platform", "Impersonate-with-audit", { platform: true }),
  perm("platform.manage_announcements", "platform", "Announcement push", { platform: true }),
  perm("platform.manage_backups", "platform", "Backup + restore-drill status", {
    platform: true,
  }),
  perm("platform.revoke_any_session", "platform", "Revoke sessions across tenants", {
    platform: true,
  }),
  perm("platform.reset_any_password", "platform", "Force password reset for any user", {
    platform: true,
  }),
  perm("platform.verify_audit_chain", "platform", "Run audit-chain verification", {
    platform: true,
  }),
] as const;

export const permissionCodes: readonly string[] = permissionCatalog.map((p) => p.code);

/**
 * Permission codes a SHIPPED vertical pack references in a `RoleTemplate`'s
 * `extra_permission_keys` whose OWNING module has not landed yet, so the code
 * is not in `permissionCatalog` (which is additive-by-module, D-028 §3) and is
 * not seeded in any migration. The `cable-wire` pack's `role.shift_boss` needs
 * these (spec 02 §2.12): the per-shift hand-off / scoreboard actor.
 *
 * They live here — NOT in `permissionCatalog` — precisely so the DB↔catalog
 * parity gate (roles.int.test.ts) stays green: `permissionCatalog` remains the
 * set of codes some migration seeds, while this is the strictly separate set of
 * codes a pack may forward-reference. The verticals cross-reference lint (spec
 * 02 §2.11) validates `extra_permission_keys` against the UNION of the two
 * (`packReferenceablePermissionCodes`), so a pack can name a real capability of
 * a not-yet-built module without a contract or migration change — the same
 * forward-reference pattern D-029 §6 used for the pack registry constant.
 *
 * Contract when the owning module lands: its migration seeds the code into
 * `permissions` and adds it to `permissionCatalog` here; the entry is removed
 * from this list in the same PR (the contracts test below fails on any overlap,
 * so it cannot rot into a duplicate).
 */
export const packForwardDeclaredPermissionCodes: readonly string[] = [
  // `production.approve_shift_handoff` LEFT this list on 2026-08-07 (slice
  // 04-C-5) — the contract above, kept. Migration 0029 seeds it, e18/e19 check
  // it, and it is in `permissionCatalog`; leaving it here as well would fail
  // the non-overlap test immediately below, which is what stops a
  // forward-declaration rotting into a duplicate after its module lands.
  "exceptions.acknowledge", // module 12 (Approvals & Exceptions) — acknowledge an exception
] as const;

/**
 * Every permission code a vertical pack's `extra_permission_keys` may reference
 * (spec 02 §2.11 lint): the seeded catalog plus the forward-declared set.
 */
export const packReferenceablePermissionCodes: ReadonlySet<string> = new Set([
  ...permissionCodes,
  ...packForwardDeclaredPermissionCodes,
]);

/** The ten seeded system roles (spec 16 §2.7), is_system=true, tenant_id NULL. */
export const systemRoleCodes = [
  "group_admin",
  "facility_admin",
  "planner",
  "sales",
  "procurement",
  "finance",
  "qc",
  "maintenance",
  "storekeeper",
  "floor_worker",
] as const;

export type SystemRoleCode = (typeof systemRoleCodes)[number];

const nonPlatformCodes = permissionCatalog.filter((p) => !p.isPlatformOnly).map((p) => p.code);

/**
 * Seeded default grants per system role (spec 16 §2.8), restricted to the
 * namespaces registered so far. When a later module registers its namespace it
 * appends both the codes and its slice of these role defaults (additive).
 *
 * group_admin = every non-platform code, by construction.
 */
export const systemRoleGrants: Readonly<Record<SystemRoleCode, readonly string[]>> = {
  group_admin: nonPlatformCodes,
  facility_admin: [
    // all identity.* except manage_security_policy, manage_roles AND
    // manage_shared_views — role management is group-level governance (§7
    // "Manage custom roles: group_admin only"; contradiction resolved in PR #6
    // review F4), and so is moderating the tenant's shared saved views, since a
    // shared view is tenant-wide and a facility-scoped admin deleting one would
    // act outside their scope (spec 16 §2.8, §7; D-305 ruling 14); all sync.*
    // (spec 16 §2.8).
    ...nonPlatformCodes.filter(
      (c) =>
        (c.startsWith("identity.") &&
          c !== "identity.manage_security_policy" &&
          c !== "identity.manage_roles" &&
          c !== "identity.manage_shared_views") ||
        c.startsWith("sync."),
    ),
    // catalogue (spec 01 §7): authoring + activate + discontinue + uom/template
    // management + cost.read. NOT product.reactivate (group_admin only), and NOT
    // schema.manage, cost.run, or cost.release — those are group-admin / finance
    // governance (§7 columns).
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.product.write",
    "catalogue.product.activate",
    "catalogue.product.discontinue",
    "catalogue.bom.write",
    "catalogue.bom.activate",
    "catalogue.routing.write",
    "catalogue.routing.activate",
    "catalogue.uom.manage",
    "catalogue.template.manage",
    "catalogue.import.run",
    "catalogue.cost.read",
    // onboarding (spec 02 §7): every onboarding code EXCEPT the group-only trio
    // install_pack / edit_terminology / go_live. facility_admin drives the
    // wizard, runs every import lane, generates QR kits, and waives/toggles the
    // checklist (non-blocking waives only; blocking waives are gated group_admin
    // at the service layer).
    "onboarding.view",
    "onboarding.manage_wizard",
    "onboarding.import.catalogue",
    "onboarding.import.customers",
    "onboarding.import.suppliers",
    "onboarding.import.opening_stock",
    "onboarding.import.machines",
    "onboarding.import.people",
    "onboarding.generate_qr_kit",
    "onboarding.checklist.waive",
    "onboarding.checklist.manual_set",
    // inventory (spec 06 §7): facility_admin holds EVERY inventory code — the
    // §7 matrix has ✓ in every facility_admin cell, incl. the cost.read field
    // grant (mirrors migration 0008's SELECT-all seed).
    ...nonPlatformCodes.filter((c) => c.startsWith("inventory.")),
    // waste (spec 05 §7): the "View ledger & variance" row is ✓F. The catalog
    // and settings WRITE codes are NOT here — that row's facility_admin cell
    // reads "targets only (own F)", i.e. recovery targets, not the catalogs.
    // 05-B: facility_admin has ✓F on every ledger/remnant action row.
    "waste.read",
    "waste.event.record",
    "waste.event.reverse",
    "waste.disposition.set",
    "waste.cost.manual",
    "waste.remnant.manage",
    // production (spec 04 §7): facility_admin has ✓ on every action row this
    // slice enforces EXCEPT "Manual cost adjustment", whose only ✓ is finance.
    ...nonPlatformCodes.filter(
      (c) => c.startsWith("production.") && c !== "production.cost.adjust",
    ),
    // quality (spec 07 §7): facility_admin has ✓F on every 07-A row EXCEPT the
    // vertical-pack seed, whose only ✓ is group_admin — pack installation is
    // group-level governance in module 02 as well (`onboarding.install_pack`).
    "quality.catalog.read",
    "quality.catalog.manage",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.inspection.record",
    "quality.inspection.cancel",
    // 07-B: "Resolve: rework" ✓F and "Holds: place manual" ✓F, plus §5's holds
    // read (facility_admin is on the list row and on D-126's money set). NOT
    // "Resolve: scrap / accept_deviation" and NOT "Holds: disposition" — both
    // cells are "—": writing off material is quality's call (+ procurement's
    // return_to_supplier narrowing on the latter, service-enforced).
    "quality.resolution.rework",
    "quality.hold.place",
    "quality.hold.read",
    // 07-C: §7's "Guided cut" row is ✓ for facility_admin. Cancel is narrowed
    // service-side to {group_admin, facility_admin, qc} on top of this same
    // code (D-163), so facility_admin holds the whole surface.
    "quality.cut.execute",
    // 07-E: facility_admin drafts certificates and manages templates; ISSUE
    // is qc's alone (+ga) — attestation is quality's signature, and a
    // facility_admin who needs one issued asks their qc, exactly as with
    // `quality.resolution.dispose` above.
    "quality.coa.draft",
    "quality.coa_template.manage",
    "quality.analytics.read",
    // sales (spec 09 §7, P3-A): facility_admin holds the whole foundation
    // surface — the ga-only cells (price-list activation, margin-guard and
    // off-cut-class edits) and the §3.4 approve threshold are service-enforced
    // NARROWINGS on these codes (D-040 two-layer), stated in each seeded
    // description.
    "sales.read",
    "sales.manage_customers",
    "sales.tier.manage",
    "sales.margin.read",
    "sales.read_value",
    "sales.margin.approve",
    "sales.credit.approve",
    // P3-C-1: facility_admin authors, dispatches and cancels documents (§7's
    // "Quote create/edit/send" and "Order cancel" rows are both ✓ for fa).
    "sales.quote.write",
    "sales.quote.send",
    "sales.order.cancel",
    // P3-C-2 (D-263): §7's "Customer price override create" row is ✓ for fa,
    // and revoke is fa/ga — the role this code's service narrowing admits.
    "sales.override.write",
    // P3-D-1 (D-283): §7's "Direct order create" row is ✓ for fa.
    "sales.order.write",
    // P3-D PR-D3 (D-286): §7's "Shipment pick/pack/ship" row is ✓ for fa
    // (and fa is one of the two lineages the cancel row admits); "Mark
    // delivered" is ✓ for fa.
    "sales.shipment.post",
    "sales.order.deliver",
    // P3-D PR-D4 (D-288): §5's override-status cell is fa (§3.6 names
    // facility_admin for both manual transitions).
    "sales.order.override_status",
    // P3-E PR-E2 (D-292 ruling 2): facility_admin writes tasks for anyone who
    // holds `sales.read` and completes any task it can read.
    "sales.activity.write",
    // P3-E2 PR E2-1 (D-312 ruling 15): §7's deal row is ✓ for fa, which reads
    // every deal (narrowing 15) and names any eligible owner (narrowing 16).
    "sales.opportunity.write",
    // planning (spec 03 §7, D-351): facility_admin holds all five P3-G codes;
    // the two MPS bars are narrowed to the group_admin lineage service-side,
    // and its approval reach to `mps_facility_admin_delta_pct` (G-3).
    "planning.read",
    "planning.demand.manage",
    "planning.settings.manage",
    "planning.mps.edit",
    "planning.mps.approve",
    // P3-H-1 (D-390 ruling 1): §7's calendars row is ✓ for fa.
    "planning.calendar.read",
    "planning.calendar.manage",
    // P3-H-2 (D-390 ruling 1): §7's MRP, planned-order view and firm rows are
    // ✓ for fa.
    "planning.mrp.run",
    "planning.planned_order.read",
    "planning.planned_order.manage",
    // P3-H-3 (D-390 ruling 1): §7's convert row is ✓ for fa.
    "planning.planned_order.convert",
    // P3-I-2 (D-406 ruling 1): §7's schedule board and solve-and-publish rows
    // are ✓ for fa.
    "planning.schedule.read",
    "planning.schedule.publish",
    // P3-I-3 (D-406 ruling 1): §7's move-money row is ✓ for fa, who also
    // holds the calendar code an overtime apply needs (D-402 ruling 5).
    "planning.cost.read",
    // P3-J J-1 (D-464 ruling 1): §7's CTP quote and commit rows are ✓ for fa.
    "planning.promise.commit",
    // P3-J J-4 (D-464 ruling 3, D-472 ruling 3): §7's scenario and promote
    // rows are ✓ for fa.
    "planning.scenario.manage",
    "planning.scenario.promote",
    // P3-J J-3 (D-464 ruling 2): spec 09 §7's Re-promise row is ✓ for fa.
    "sales.order.re_promise",
    // messaging (spec 17 §7, P3-B part 1): facility_admin holds the WHOLE
    // part-1 surface — connect/disconnect, settings + health, linking,
    // identities, templates, send, preferences, and the address field grant
    // (§7's field-redaction row lists fa on the full-value side).
    ...nonPlatformCodes.filter((c) => c.startsWith("messaging.")),
    // agents (spec 14 §7.1, D-513 ruling 1): read, invoke, manage and
    // activity.read; config.manage is group_admin alone.
    "agents.read",
    "agents.invoke",
    "agents.manage",
    "agents.activity.read",
  ],
  // planner authors + activates products/BOMs/routings and runs imports (spec
  // 01 §7); product lifecycle is activate-only (no discontinue/reactivate —
  // §3.1). cost.read is grantable but off by default. Every office/floor role
  // reads the catalogue (§7 read row — floor_worker floor-lookup only).
  planner: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "sync.view_fleet",
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.product.write",
    "catalogue.product.activate",
    "catalogue.bom.write",
    "catalogue.bom.activate",
    "catalogue.routing.write",
    "catalogue.routing.activate",
    "catalogue.import.run",
    // onboarding (spec 02 §7): sees the wizard + runs the catalogue import lane.
    "onboarding.view",
    "onboarding.import.catalogue",
    // inventory (spec 06 §7): read + reservations + transfer CREATE (the
    // create-only cell is service-enforced on transfer.manage) + trace.
    "inventory.read",
    "inventory.reservation.manage",
    "inventory.transfer.manage",
    "inventory.trace.read",
    // waste (spec 05 §7): "View ledger & variance" ✓F (facility scope applied
    // service-side). 05-B: the "Reserve/release/reclass/scrap remnant" row's
    // planner cell is "reserve/release F" — one code, per-action narrowing
    // service-enforced (D-040 two-layer).
    "waste.read",
    "waste.remnant.manage",
    // production (spec 04 §7): the planner authors and drives work orders —
    // create/edit, release, complete, skip + the §3.11 office-recorded result,
    // and reads costs (the "View WO costs" row is ✓). NOT close (that row is
    // group_admin/facility_admin/finance) and NOT the manual adjustment.
    // `production.wo.cancel` IS here because §5 row 7 reads "facility_admin
    // (draft/released also planner)" — §7's matrix row covers the in_progress
    // cancel only. The draft/released narrowing is service-enforced on top of
    // the code (D-040 two-layer), never a second code.
    "production.read",
    "production.wo.write",
    "production.wo.release",
    "production.wo.complete",
    "production.wo.cancel",
    "production.operation.record",
    "production.view_costs",
    // 04-C-1: the §7 telemetry-read row (drift charts + downtime logs) has a
    // planner ✓ — one code for the one role set (D-074).
    "production.telemetry.read",
    // 04-C-4: the §7 "View scoreboard (office, with all shifts)" row has a
    // planner ✓. No piece-rate grant — that row's ✓s are ga/fa/finance.
    "production.scoreboard.read",
    // quality (spec 07 §7): the planner READS the library and the queue (both
    // rows are ✓) and, from 07-B, holds §7's `Resolve: rework` ✓ — the
    // sub-decision D-123 ruling 3 left open (what a planner's rework does to a
    // HELD operation) is settled in the code's own seeded description: refused
    // without production.qc_hold.release, service-enforced. Plus §5's holds
    // read: the planner is on the list row AND on D-126's money set — the
    // quarantine board's value-at-risk ranking is planner-visible by ruling.
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.resolution.rework",
    "quality.hold.read",
    // 07-E: §5's fpy/pareto cells include planner.
    "quality.analytics.read",
    // sales (spec 09 §7, P3-A): planner READS the customer book and order
    // surfaces (§4.1/§4.7 role lists) — no money values (`sales.read_value`
    // withheld: §7's redaction row 2 lists planner as redacted) and no margin
    // fields.
    "sales.read",
    // messaging (spec 17 §7, P3-B part 1): the three all-role self-service
    // codes (own link code / own revoke / own preference — the customer
    // halves are the D-040 service narrowings) + the template catalog view
    // (§7's view row lists planner). NO address field grant: planner reads
    // addresses MASKED (§7 field redaction).
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
    "messaging.template.read",
    // part 2 (0054): §7's "View conversations + messages" row is read-only ✔
    // for planner — the inbox read code, never the manage code, so the
    // composer/assign/close surface stays refused at the route gate.
    "messaging.conversation.read",
    // planning (spec 03 §7, D-351): reads every P3-G surface, edits the
    // master data and the MPS cells and submits a plan (G-3); the settings and
    // the approval are the admins'.
    "planning.read",
    "planning.demand.manage",
    "planning.mps.edit",
    // P3-H-1 (D-390 ruling 1): §7's calendars row is R for planner.
    "planning.calendar.read",
    // P3-H-2 (D-390 ruling 1): §7's MRP, planned-order view and firm rows are
    // ✓ for planner.
    "planning.mrp.run",
    "planning.planned_order.read",
    "planning.planned_order.manage",
    // P3-H-3 (D-390 ruling 1): §7's convert row is ✓ for planner.
    "planning.planned_order.convert",
    // P3-I-2 (D-406 ruling 1): §7's schedule board and solve-and-publish rows
    // are ✓ for planner.
    "planning.schedule.read",
    "planning.schedule.publish",
    // P3-J J-1 (D-464 ruling 1): §7's CTP quote and commit rows are ✓ for
    // planner.
    "planning.promise.commit",
    // P3-J J-4 (D-464 ruling 3): §7's scenario row is ✓ for planner; promote
    // is not (D-472 ruling 3).
    "planning.scenario.manage",
    // P3-J J-3 (D-464 ruling 2): spec 09 §7's Re-promise row is ✓ for
    // planner, who re-promises a line and withdraws a committed promise.
    "sales.order.re_promise",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  // sales sees the wizard + runs the customer / open-sales-order import lane
  // (spec 02 §7); inventory (spec 06 §7): read + reservations (own orders,
  // service-enforced) + trace (own customers, service-enforced).
  sales: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "catalogue.read",
    "onboarding.view",
    "onboarding.import.customers",
    "inventory.read",
    "inventory.reservation.manage",
    "inventory.trace.read",
    // production (spec 04 §7): the "View WO list/detail" row is ✓ for sales
    // with "own-book MTO only" — a service-side filter that matches NOTHING.
    // In P2 the column it read, `sales_order_line_id`, was NULL on every row
    // (D-071); since PR-D6 a work order can be pegged, and D-290 ruling 9
    // closed the arm outright rather than let it admit every pegged work order
    // in the tenant, until P3-H lands the real own-book fence. A rep sees their
    // orders' work orders as chips on the order (spec 09 §4.7). The grant is
    // seeded because the matrix says so (the description above is seeded
    // verbatim by 0019 and states the eventual cell); no cost code, so every
    // `*_minor` key is stripped from what it does see.
    "production.read",
    // waste (spec 05 §7, 05-B): "Reserve/release/reclass/scrap remnant" —
    // sales' cell is "reserve/release/reclass F"; register/scrap are refused
    // service-side. sales never sees `carried_cost_minor` (§7 redaction).
    "waste.remnant.manage",
    // quality (spec 07 §7): the "Inspections: read" row's sales cell is "final
    // only" — one code, the stage narrowing service-side (D-040 two-layer), and
    // it is what `GET /ship-readiness` rides. NO `quality.catalog.read`: the
    // control-points read row's sales cell is "—". From 07-B, §5's holds read:
    // sales is on the list row (a held unit is an unshippable promise), and
    // NOT on D-126's money set — `value_at_risk_minor` and `currency` are both
    // absent from every hold a sales session reads.
    "quality.inspection.read",
    "quality.hold.read",
    // 07-E: sales DRAFT and read certificates for their customers (§5's
    // drafter cell); they never attest them — issue stays qc's.
    "quality.coa.draft",
    // sales (spec 09 §7, P3-A): the working set of the role the module is
    // named for — read + customer/contact/channel authoring, BOTH narrowed to
    // ASSIGNED customers via `customer_assignments` service-side (§7's scope
    // note), and the §7-default `sales.read_value` field grant. NO
    // `sales.margin.read` (§7: sales sees margins only WITH the grant,
    // tenant-granted), NO `sales.tier.manage` (tier + pricing governance is
    // admin work), NO approve codes.
    "sales.read",
    "sales.manage_customers",
    "sales.read_value",
    // P3-C-1: the document surface the role exists for — §7's "Quote
    // create/edit/send" row is ✓ sales, and "Order cancel" is ✓ sales
    // (confirmed only, own). Both narrowings are SERVICE-side on these same
    // codes (D-040 two-layer), never a second code.
    "sales.quote.write",
    "sales.quote.send",
    "sales.order.cancel",
    // P3-C-2 (D-263): §7's "Customer price override create" row is ✓ sales
    // (assigned customers; a breach auto-routes to approval). The SAME code
    // gates revoke, which the service narrows to fa/ga — a sales user's revoke
    // answers 403 naming the role (§10 M09-S16), never a missing grant here.
    "sales.override.write",
    // P3-D-1 (D-283): §7's "Direct order create" row is ✓ sales (assigned
    // customers — the D-224 lineage narrowing answers 404 on any other, the
    // same service-side layer the quote create uses; never a second code).
    "sales.order.write",
    // P3-D PR-D3 (D-286): §5's `mark-delivered` cell names sales — a manager
    // records the carrier's word for their own customers (assigned,
    // service-narrowed). NO `sales.shipment.post`: picking is the floor's
    // (§7's row has no sales cell), and a manager who needs a shipment opened
    // asks the warehouse.
    "sales.order.deliver",
    // P3-E PR-E2 (D-292 ruling 2): the manager's own tasks — create for
    // themselves only, complete what is assigned to them (both SERVICE
    // narrowings on this one code, D-040 two-layer).
    "sales.activity.write",
    // P3-E2 PR E2-1 (D-312 ruling 15): §7's deal row is ✓ sales (assigned
    // customers; deals it reads) — narrowing 16 is the SERVICE's, never a
    // second code, and only an admin names another owner.
    "sales.opportunity.write",
    // messaging (spec 17 §7, P3-B part 1): sales is the role the channel
    // layer serves — linking customers (the customer-contact cell of the
    // issue/revoke rows is theirs), identity management, template authoring
    // (§7 manage row), the channel-abstracted send, preferences, and FULL
    // addresses (§7's field-redaction row lists sales on the full-value
    // side). NOT account.manage / settings.read (ga/fa governance).
    "messaging.link.issue",
    "messaging.identity.manage",
    "messaging.identity.revoke",
    "messaging.template.read",
    "messaging.template.manage",
    "messaging.send",
    "messaging.preferences.manage",
    "messaging.address.read",
    // part 2 (0054): the inbox is sales' working surface — both conversation
    // codes (§7's reply/assign/snooze/close/claim rows are all ✔ sales).
    "messaging.conversation.read",
    "messaging.conversation.manage",
    // planning (spec 03 §7, D-351): reads the pool, forecasts and plans.
    "planning.read",
    // P3-I-2 (D-406 ruling 1): §7's schedule board row is R for sales.
    "planning.schedule.read",
    // P3-J J-1 (D-464 ruling 1): §7's CTP quote, commit and quoted-withdraw
    // rows are ✓ for sales; a committed withdraw is not (D-464 ruling 2).
    "planning.promise.commit",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  // procurement authors raw/consumable products only (kind-scoped at the
  // service layer, spec 01 §7) and reads BOM structure (§7 "Read BOM
  // structure"); sees the wizard + runs the supplier / open-PO import lane
  // (spec 02 §7).
  procurement: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.product.write",
    "onboarding.view",
    "onboarding.import.suppliers",
    // inventory (spec 06 §7): read + receipts + reservations + transfer CREATE
    // (create-only, service-enforced) + trace. Cost visibility on PO movements
    // only is a serializer rule (§7 field redactions), not a field grant here.
    "inventory.read",
    "inventory.receipt.create",
    "inventory.reservation.manage",
    "inventory.transfer.manage",
    "inventory.trace.read",
    // quality (spec 07 §7): procurement READS the control-point library (✓ —
    // it is the receiving checks their suppliers are judged by) and the
    // inspection queue with a "receiving only" narrowing applied service-side.
    // From 07-B: the "Holds: disposition" row's procurement cell is
    // "return_to_supplier only" — one code, the disposition-value narrowing
    // service-enforced (D-040 two-layer, stated in the seeded description);
    // its route is `POST /holds/{id}/disposition` (mounted by 07-B-2). Plus
    // §5's holds read (procurement is on the list row, NOT on D-126's money
    // set — both money keys absent).
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.hold.disposition",
    "quality.hold.read",
    // 07-E: the supplier receiving panel is procurement's (§5) — the other
    // three analytics routes refuse them service-side (D-040).
    "quality.analytics.read",
    // messaging (spec 17 §7, P3-B part 1): the three all-role self-service
    // codes only (own link code / own revoke / own preference — §7's "own"
    // cells span the whole matrix; the customer halves are service-narrowed).
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
    // planning (spec 03 §7, D-351): reads the pool, forecasts and plans.
    "planning.read",
    // P3-H-2 (D-390 ruling 1, D-387 ruling 5): §7's planned-order view row is
    // ✓ for procurement, narrowed to purchase orders at every read.
    "planning.planned_order.read",
    // P3-H-3 (D-390 ruling 1, D-389 ruling 7): §7's convert row is ✓ for
    // procurement, purchase orders only; a manufacture or transfer id it sends
    // reads as absent.
    "planning.planned_order.convert",
    // P3-I-2 (D-406 ruling 1): §7's schedule board row is R for procurement.
    "planning.schedule.read",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  finance: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "sync.resolve_drift",
    "catalogue.read",
    "catalogue.structure.read",
    "catalogue.cost.read",
    "catalogue.cost.run",
    "catalogue.cost.release",
    // onboarding (spec 02 §7): sees the wizard (its finance role is the
    // co-permission that unlocks opening_stock value posting at commit —
    // enforced service-side, not a distinct onboarding grant).
    "onboarding.view",
    // inventory (spec 06 §7): read + reverse movements + count review + trace
    // + the cost field grant (movement/level/history cost columns, variance
    // values).
    "inventory.read",
    "inventory.movement.reverse",
    "inventory.count.review",
    "inventory.trace.read",
    "inventory.cost.read",
    // waste (spec 05 §7): "View ledger & variance" ✓ (group-wide for finance).
    // 05-B: finance records MANUAL events (the record row's finance cell reads
    // "✓ F (manual)" — the source narrowing is service-enforced), reverses,
    // dispositions, and is one of the three roles on "Manual unit cost".
    "waste.read",
    "waste.event.record",
    "waste.event.reverse",
    "waste.disposition.set",
    "waste.cost.manual",
    // production (spec 04 §7): finance reads work orders, CLOSES them (the
    // "Close WO" row is group_admin/facility_admin/finance), sees costs, is
    // the ONLY role on the "Manual cost adjustment" row, and — from 04-B-2 —
    // posts manual labor corrections (§5 row 31's role list: facility_admin +
    // finance; labor is money).
    "production.read",
    "production.wo.close",
    "production.view_costs",
    "production.cost.adjust",
    "production.labor.adjust",
    // 04-C-4: §7's scoreboard row has a finance ✓, and BOTH piece-rate rows do
    // — finance is the role the piece-rate capture exists for (§12 Q1's rate
    // table is future module-11 work and finance is who will price it).
    "production.scoreboard.read",
    "production.piece_rate.read",
    // quality (spec 07 §7): the "Inspections: read" row has a finance ✓ — the
    // money at risk on a failed inspection is a finance number. NO
    // `quality.catalog.read`: the control-points read row's finance cell is "—".
    // From 07-B, §5's holds read: finance is on the list row and on D-126's
    // money set — value at risk is money tied up in held stock, which is
    // precisely the finance read of the quarantine board.
    "quality.inspection.read",
    "quality.hold.read",
    // 07-E: §5's fpy/pareto cells include finance (the money the cube ranks
    // is their question).
    "quality.analytics.read",
    // sales (spec 09 §7, P3-A): finance reads the customer book (§4.1's role
    // list), edits CREDIT limit/terms/hold through the same PATCH route the
    // profile editors use (the §7 credit cell — field-group narrowing
    // service-enforced on `sales.manage_customers`, D-040 two-layer), sees
    // margins and money (§7 redaction rows 1–2 both list finance), and
    // decides credit releases incl. recording out-of-band prepayments
    // (§2.16/D-231).
    "sales.read",
    "sales.manage_customers",
    "sales.margin.read",
    "sales.read_value",
    "sales.credit.approve",
    // messaging (spec 17 §7, P3-B part 1): the three all-role self-service
    // codes + the template catalog view (§7's view row lists finance). No
    // address field grant — finance reads addresses masked (§7).
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
    "messaging.template.read",
    // part 2 (0054): §7's conversations row is read-only ✔ for finance
    // (quote threads carry money a finance read may see — §7's own note);
    // never the manage code.
    "messaging.conversation.read",
    // planning (spec 03 §7, D-351): reads the pool, forecasts and plans.
    "planning.read",
    // P3-H-2 (D-390 ruling 1): §7's planned-order view row is R for finance,
    // every kind.
    "planning.planned_order.read",
    // P3-I-2 (D-406 ruling 1): §7's schedule board row is R for finance.
    "planning.schedule.read",
    // P3-I-3 (D-406 ruling 1): §7's move-money row is R for finance, which
    // reads the moves through this field grant alone (D-402 ruling 6).
    "planning.cost.read",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  qc: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "sync.use_floor",
    "catalogue.read",
    "catalogue.structure.read",
    "onboarding.view",
    // inventory (spec 06 §7): read + guided qc_fail cuts (service-enforced) +
    // scan/re-measure + quarantine/release + trace.
    "inventory.read",
    "inventory.unit.cut",
    "inventory.scan.locate",
    "inventory.quarantine",
    "inventory.trace.read",
    // waste (spec 05 §7): "View ledger & variance" ✓F with NO cost — the money
    // columns are dropped by the serializer, not by a second code. 05-B: qc
    // records (✓F), dispositions (✓F) and registers remnants (✓F on the
    // register row; the lifecycle actions are refused service-side).
    "waste.read",
    "waste.event.record",
    "waste.disposition.set",
    "waste.remnant.manage",
    // production (spec 04 §7): "View WO list/detail" ✓, with no cost code — so
    // a qc session's `GET /v1/work-orders/:id` carries no `*_minor` key at all,
    // which is what M04-S12 asserts at the API response rather than the UI.
    "production.read",
    // 04-C-4: §7's scoreboard row has a qc ✓ — quality watches first-pass
    // yield, which is the column the row exists for. Still no cost code, so
    // nothing on that surface carries money (it has none — §2.10).
    "production.scoreboard.read",
    // 04-C-5: §7's "Dispose defect hand-off" row is group_admin / facility_admin
    // / qc, and e32's row is the same three. Quality decides whether a defective
    // drum is reworked, scrapped or accepted — and that decision is what moves
    // the number onto the PRODUCING shift's board (§3.7).
    //
    // §7's "Flag defect hand-off" row ALSO has a qc ✓ and qc holds nothing for
    // it, which is a stated P2 limitation rather than an omission: §5 gives
    // defect flagging no endpoint of its own — it rides e13, the device batch,
    // on `production.floor.record`, which qc does not hold and which D-079
    // binds to a floor session. A non-device flag route is module 07/18-adjacent
    // successor work, recorded in spec 04 §7 rather than invented here.
    "production.approve_shift_handoff",
    "production.qc_hold.release",
    // quality (spec 07 §7): qc is the role this module exists for and holds
    // every 07-A code except the vertical-pack seed (group_admin alone), and
    // every 07-B code — the two resolutions, both hold rows and the holds
    // read: §7's "Resolve: scrap / accept_deviation" and "Holds: disposition"
    // rows have qc as their ONLY unnarrowed ✓, and qc is on D-126's money set
    // (an inspector choosing scrap-vs-rework with the magnitude hidden is the
    // failure that ruling names). 07-C's guided cut and 07-E's CoA lifecycle
    // still arrive with their endpoints, never before them.
    "quality.catalog.read",
    "quality.catalog.manage",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.inspection.record",
    "quality.inspection.cancel",
    "quality.resolution.rework",
    "quality.resolution.dispose",
    "quality.hold.place",
    "quality.hold.disposition",
    "quality.hold.read",
    // 07-C: §7's "Guided cut" row — qc holds the whole surface, and qc is one
    // of the three roles the cancel narrowing keeps (D-163).
    "quality.cut.execute",
    // 07-E: qc holds the WHOLE CoA surface — draft, template manage, and the
    // issue/revoke pair that is theirs alone (+ga): a certificate is quality's
    // signature on the product.
    "quality.coa.draft",
    "quality.coa.issue",
    "quality.coa_template.manage",
    "quality.analytics.read",
    // sales (spec 09 §7, P3-A): §7's "View quotes/orders read-only" row has a
    // qc ✓ (a failed final inspection blocks a shipment somebody promised —
    // qc needs to see whose). No money, no margins: qc holds neither field
    // grant, so every `*_minor` key is absent from what it reads.
    "sales.read",
    // messaging (spec 17 §7, P3-B part 1): the three all-role self-service
    // codes only (own link code / own revoke / own preference).
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
    // part 2 (0054): §7's conversations row is read-only ✔ for qc; never the
    // manage code.
    "messaging.conversation.read",
    // planning (spec 03 §7, D-406 ruling 1): §7's schedule board row is R
    // for qc, which holds no `planning.read` and so never reads the pool.
    "planning.schedule.read",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  maintenance: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "sync.use_floor",
    "catalogue.read",
    "onboarding.view",
    // inventory (spec 06 §7): read + scan/re-measure.
    "inventory.read",
    "inventory.scan.locate",
    // waste (spec 05 §7): the view row's maintenance cell is "machine variance
    // only" — one code, the narrowing service-side.
    "waste.read",
    // production (spec 04 §7): "View WO list/detail" ✓, no cost — and, since
    // 04-C-1, the telemetry read maintenance actually wants: e22's downtime
    // logs now, e20/e21's drift when 04-C-2 mounts them (same code, same §7
    // role set).
    "production.read",
    "production.telemetry.read",
    // 04-C-4: §7's scoreboard row has a maintenance ✓ — `downtime_minutes` per
    // shift and work centre is the maintenance read of that surface.
    "production.scoreboard.read",
    // quality (spec 07 §7): maintenance is the ONE role whose every cell in the
    // §7 matrix is "—", so it holds no `quality.*` code at all. Stated rather
    // than left as an absence, because an empty cell and a forgotten row look
    // identical in a diff.
    // messaging (spec 17 §7, P3-B part 1): maintenance is on §1's staff-
    // linking persona list — the three all-role self-service codes.
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
    // planning (spec 03 §7, D-390 ruling 1): §7's calendars row is R for
    // maintenance, which holds no `planning.read` and reads the calendars
    // screen and the preview on this code alone (P3-H-1).
    "planning.calendar.read",
    // P3-I-2 (D-406 ruling 1): §7's schedule board row is R for maintenance.
    "planning.schedule.read",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  // storekeeper sees the wizard, validates opening-stock imports, and generates
  // QR kits (stock-unit + location labels) (spec 02 §7).
  storekeeper: [
    "identity.view_org",
    // D-442: co-members' names on the records this role reads.
    "identity.view_member_names",
    "sync.use_floor",
    "catalogue.read",
    "onboarding.view",
    "onboarding.import.opening_stock",
    "onboarding.generate_qr_kit",
    // inventory (spec 06 §7): the warehouse operator set — everything except
    // movement reversal, quarantine, count review (grantable, not default) and
    // cost fields. Threshold/creation narrowings are service-enforced.
    "inventory.read",
    "inventory.location.manage",
    "inventory.receipt.create",
    "inventory.unit.cut",
    "inventory.scan.locate",
    "inventory.adjust",
    "inventory.reservation.manage",
    "inventory.transfer.manage",
    "inventory.transfer.receive",
    "inventory.count.perform",
    "inventory.trace.read",
    "inventory.count_schedule.manage",
    // production (spec 04 §7): "View WO list/detail" ✓ and "Material issue /
    // return" ✓ — the storekeeper is the role §5 row 30 exists for. No cost
    // code, so `unit_cost_minor` is stripped from the issue it just posted.
    "production.read",
    "production.material.issue",
    // waste (spec 05 §7, 05-B): record (✓F), disposition (✓F), and the whole
    // remnant register/lifecycle row (✓F). No `waste.read` — the storekeeper's
    // "View ledger & variance" cell is "—"; the remnant registry rides
    // `waste.remnant.manage`, and money stays serializer-redacted.
    "waste.event.record",
    "waste.disposition.set",
    "waste.remnant.manage",
    // quality (spec 07 §7): the storekeeper reads the library and the queue and
    // CREATES ad-hoc inspections (✓ on that row — a bobbin that looks wrong on
    // the rack is checked by whoever is holding it), but does not record
    // results: the "Record results / complete" row's storekeeper cell is "—".
    // From 07-B: "Holds: place manual" ✓ — quarantining a suspect drum is the
    // warehouse act the row exists for. NO `quality.hold.read`: §5's holds
    // list row has no storekeeper (they place a hold and hand the BOARD to
    // quality; the placed hold comes back in the POST response).
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.hold.place",
    // sales (spec 09 §7, P3-A): §7's "View quotes/orders read-only" row has a
    // storekeeper "ship-relevant" cell — the §4.8 ship queue is an order read.
    // The ship-relevant narrowing is service-enforced on this code (D-040);
    // no field grants, so the pick screen shows qty only (§7 redaction row 2's
    // own words).
    "sales.read",
    // P3-D PR-D3 (D-286): §7's "Shipment pick/pack/ship" row is ✓ storekeeper
    // (own facility) — the role §4.8's ship queue and pick screen exist for;
    // and §5's `mark-delivered` cell names the storekeeper too (own facility).
    // Both facility narrowings are service-side (D-040) on top of the D-074
    // RESTRICTIVE fence migration 0063 puts on both shipment tables. NOT the
    // mis-scan override (fa/ga only — a service narrowing on
    // `sales.shipment.post`, never a code) and NOT shipment cancel (same).
    "sales.shipment.post",
    "sales.order.deliver",
    // messaging (spec 17 §7, P3-B part 1): the three all-role self-service
    // codes only (own link code / own revoke / own preference).
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
    // planning (spec 03 §7, D-406 ruling 1): §7's schedule board row is R
    // for storekeeper, which holds no `planning.read`.
    "planning.schedule.read",
    // agents (spec 14 §7.1, D-513 ruling 1): trigger its own domain's agent.
    "agents.invoke",
  ],
  // floor_worker has NO onboarding access (— on the §7 view row; invites are
  // only dispatched at go-live, so floor workers do not exist pre-live).
  // inventory (spec 06 §7): read + cut + scan/re-measure + transfer receive +
  // counts (record-only, service-enforced). No trace — §7 gives floor_worker
  // 403 on trace endpoints.
  floor_worker: [
    "identity.view_org",
    "sync.use_floor",
    "catalogue.read",
    "inventory.read",
    "inventory.unit.cut",
    "inventory.scan.locate",
    "inventory.transfer.receive",
    "inventory.count.perform",
    // production (spec 04 §7): "View WO list/detail" ✓ with "own station ops
    // only" — service-narrowed, and the station-queue read (§5 row 14) is what
    // it is for. From 04-B-2: `production.floor.record` (the §7 "Floor events"
    // row — own-dept stations, service-narrowed) and the footnote-ᴬ grant of
    // `production.material.issue`, which 0019 DELIBERATELY WITHHELD because
    // its only 04-A route was e30's office-side manual issue; e13's scan-issue
    // path — the ✓ that cell always meant — is 04-B-2's server surface, so the
    // grant arrives with the endpoint that justifies it (migration 0022).
    // e30 itself stays fenced from floor_worker at the service layer (its §5
    // role list is storekeeper/facility_admin/group_admin — D-040 two-layer).
    "production.read",
    "production.floor.record",
    "production.material.issue",
    // From 04-C-4: the §7 scoreboard row's floor_worker cell ("wall-mode/floor
    // variant") and the two piece-rate rows' "own rows only" cells. Both are
    // narrowings of the same capability, not different ones: the scoreboard has
    // no money to redact (§2.10) and the RLS facility fence already limits which
    // rows a department-scoped floor session can read, while e27 filters to the
    // session principal's OWN entries service-side (D-079's identity rule, the
    // same shape as e16). The S12 wall board rides this grant on an ordinary
    // floor session — D-087 ruling 1's interim, with the scoped wall token named
    // as module-18-adjacent successor work and `floor_devices` never an auth
    // factor (D-079 pt 3).
    "production.scoreboard.read",
    "production.piece_rate.read",
    // waste (spec 05 §7, 05-B): the §4 S8 3-tap capture (✓D, own station —
    // service-narrowed) and remnant register/update (✓D). No `waste.read`;
    // no disposition code (the floor's disposition is the category DEFAULT,
    // applied by the capture path, never a decision endpoint).
    "waste.event.record",
    "waste.remnant.manage",
    // quality (spec 07 §7): the floor worker EXECUTES inspections — §4.4 is
    // their screen. Reads the library (its "assigned stage" narrowing is
    // service-side), reads the queue for their own facility (which the RLS
    // facility fence already enforces at the database floor), creates ad-hoc
    // inspections and records results. No cancel — that row's cell is "—", and
    // a worker who cannot pass a check must fail it, not delete it.
    "quality.catalog.read",
    "quality.inspection.read",
    "quality.inspection.create",
    "quality.inspection.record",
    // 07-C: §7's "Guided cut" row is "✓ (not cancel)" for floor_worker — the
    // person standing at the saw proposes, confirms and executes. The "not
    // cancel" half is a SERVICE narrowing on top of this one code (D-163), not
    // a second code: D-074's rule is one code per DISTINCT role set, and a
    // floor worker who proposes is never stranded by the narrowing because
    // D-162's supersede rule means their next propose replaces the live one.
    "quality.cut.execute",
    // 07-E: §5's scoreboard cell — "same + floor_worker (own facility, no
    // value fields)"; the other three analytics routes refuse them
    // service-side (D-040).
    "quality.analytics.read",
    // messaging (spec 17 §7, P3-B part 1): §7's "Issue link code for own user
    // identity" and "Revoke any user identity: own" rows are all-✓ including
    // floor_worker (spec 17 §1 — every internal staff persona links their own
    // Telegram; the 12-routed traffic arrives P4). Self-only enforced
    // service-side, as everywhere.
    "messaging.link.issue",
    "messaging.identity.revoke",
    "messaging.preferences.manage",
  ],
};

export function isValidPermissionCode(code: string): boolean {
  return PERMISSION_CODE_GRAMMAR.test(code);
}

const catalogByCode = new Map(permissionCatalog.map((p) => [p.code, p]));

export function getPermission(code: string): PermissionDef | undefined {
  return catalogByCode.get(code);
}
